import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  Stack,
  IconButton,
  MenuItem,
  FormControl,
  FormLabel,
  FormHelperText,
  Avatar,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Collapse,
  Fab,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  createFilterOptions,
  Divider,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
  CircularProgress,
  useMediaQuery,
  SxProps,
  Theme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import PaymentIcon from '@mui/icons-material/Payment';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOffOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { PREMIUM_COLORS, PERSON_COLORS, personColor, tokens } from '../../theme';
import { EmptyState, LoadingState, StatusPill, BookingStatusPill, PersonAvatar, useConfirm } from '../../components/ui';
import {
  formatDateLong,
  formatWeekdayDay,
  formatDayMonth,
  formatMonthYear,
  formatMoney,
  formatNumber,
  formatDuration,
  countLabel,
} from '../../utils/format';
import { apiClient } from '../../services/api';
import { bookingApi, Booking, PaymentMethod } from '../../services/bookingService';
import { staffApi, Staff, StaffScheduleException } from '../../services/staffService';
import { inventoryApiService } from '../../services/inventoryService';
import StaffScheduleDialog from '../../components/StaffScheduleDialog';
import { serviceApi, Service } from '../../services/serviceService';
import { clientApi, Client, CreateClientInput } from '../../services/clientService';
import { salonApi, enabledPaymentMethods } from '../../services/salonService';
import { absenceApi, Absence } from '../../services/employeeService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSalonContextStore } from '../../store/salonContextStore';
import { useAuthStore } from '../../store/authStore';
import { WorkingHoursEditor } from '../../components/WorkingHoursEditor';

// ─── TYPES ──────────────────────────────────────────────
interface StaffScheduleEntry {
  dayOfWeek: number; // 0=Sun, 1=Mon, ..., 6=Sat
  startTime: string;
  endTime: string;
  isWorking: boolean;
}

interface StaffMember {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  avatar: string;
  color: string;
  visible: boolean;
  serviceIds: string[];
  schedules: StaffScheduleEntry[];
}

interface CalendarEvent {
  id: string;
  date: string; // YYYY-MM-DD
  startHour: number;
  startMin: number;
  endHour: number;
  endMin: number;
  service: string;
  serviceId: string;
  /** Wybrany wariant usługi, gdy usługa je ma. */
  serviceVariantId?: string;
  clientName: string;
  clientPhone: string;
  clientId?: string;
  staffId: string;
  status: 'CONFIRMED' | 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  price: number;
  paid: boolean;
  paymentMethod?: PaymentMethod;
  notes?: string;
}

// Payment methods offered in the appointment detail view
const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Gotówka' },
  { value: 'CARD', label: 'Karta' },
  { value: 'BLIK', label: 'BLIK' },
  { value: 'TRANSFER', label: 'Przelew' },
  { value: 'BARTER', label: 'Barter' },
  { value: 'VOUCHER', label: 'Bon' },
];

interface ServiceOption {
  id: string;
  name: string;
  duration: number;
  price: number;
  durationMax?: number | null;
  priceMax?: number | null;
  variants?: { id: string; name: string; price: number; duration: number }[];
}
interface ProductOption { id: string; name: string; unit: string; price: number }

// Pozycja dolozona przy finalizacji — usluga ALBO produkt (uwagi klienta 16 i 19).
interface ExtraItem {
  key: string;
  serviceId?: string;
  productId?: string;
  name: string;
  quantity: number;
  unitPrice: number;
}
interface ClientOption { id: string; name: string; phone: string }

// Sentinel option appended to the client dropdown so a not-found client can be
// created inline, without closing the "new booking" dialog.
const ADD_CLIENT_SENTINEL_ID = '__add_new_client__';

// Zwracane przez onSave, gdy backend poprosil o potwierdzenie zapisu mimo
// ostrzezen. Formularz ma wtedy zostac nietkniety — decyzja zapada w osobnym
// oknie, a nie przez komunikat bledu.
const PENDING_CONFIRMATION = '__pending_confirmation__';

// Nieobecnosci pokazywane w kalendarzu (uwaga klienta 14: choroba, urlop,
// wyjscie — wpisy inne niz wizyta klienta).
const ABSENCE_LABELS: Record<Absence['type'], string> = {
  SICK_LEAVE: 'L4 / choroba',
  VACATION: 'Urlop',
  EXCUSED: 'Nieobecność usprawiedliwiona',
  UNEXCUSED: 'Nieobecność nieusprawiedliwiona',
  OTHER: 'Nieobecność',
};

interface BookingWarning {
  code: string;
  message: string;
}
const clientFilterOptions = createFilterOptions<ClientOption>();

// ─── STAFF COLORS ───────────────────────────────────────
// Kolor pracownika bierzemy z personColor(id), tak jak awatary w reszcie
// aplikacji. W kalendarzu kolor rozroznia osoby, wiec gdy dwie trafia na ten
// sam odcien, kolejna dostaje pierwszy wolny kolor z palety.
const assignStaffColors = (list: Staff[]): Record<string, string> => {
  const used = new Set<string>();
  const result: Record<string, string> = {};
  list.forEach((s) => {
    let color = personColor(s.id);
    if (used.has(color)) color = PERSON_COLORS.find((c) => !used.has(c)) || color;
    used.add(color);
    result[s.id] = color;
  });
  return result;
};
const STAFF_VISIBILITY_STORAGE_PREFIX = 'visitio_calendar_staff_visibility';

const toKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getCurrentUserId = () => {
  try {
    const raw = localStorage.getItem('visitio_user');
    if (!raw) return 'anon';
    const parsed = JSON.parse(raw);
    return parsed?.id || 'anon';
  } catch {
    return 'anon';
  }
};

const getStaffVisibilityStorageKey = (salonId: string) =>
  `${STAFF_VISIBILITY_STORAGE_PREFIX}:${salonId}:${getCurrentUserId()}`;

// Convert backend Booking to CalendarEvent
const bookingToEvent = (b: Booking): CalendarEvent => {
  const [sh, sm] = (b.startTime || '09:00').split(':').map(Number);
  const [eh, em] = (b.endTime || '10:00').split(':').map(Number);
  // date comes as ISO string or YYYY-MM-DD
  const dateStr = typeof b.date === 'string' ? b.date.substring(0, 10) : toKey(new Date(b.date));

  return {
    id: b.id,
    date: dateStr,
    startHour: sh,
    startMin: sm,
    endHour: eh,
    endMin: em,
    service: b.service?.name || 'Usługa',
    serviceId: b.serviceId || '',
    clientName: b.client ? `${b.client.firstName} ${b.client.lastName}` : 'Klient',
    clientPhone: b.client?.phone || '',
    clientId: b.clientId,
    staffId: b.staffId || '',
    status: b.status as CalendarEvent['status'],
    price: Number(b.totalPrice) || Number(b.service?.price) || 0,
    paid: (b as any).payment?.status === 'COMPLETED',
    paymentMethod: (b as any).payment?.method || undefined,
    notes: b.notes,
  };
};

// Convert backend Staff to StaffMember
const staffToMember = (s: Staff, color: string): StaffMember => ({
  id: s.id,
  name: `${s.firstName} ${s.lastName}`,
  firstName: s.firstName,
  lastName: s.lastName,
  avatar: `${s.firstName[0]}${s.lastName[0]}`,
  color,
  visible: true,
  serviceIds: s.services?.map((ss) => ss.service.id) || [],
  schedules: (s.schedules || []).map((sc) => ({
    dayOfWeek: sc.dayOfWeek,
    startTime: sc.startTime,
    endTime: sc.endTime,
    isWorking: sc.isWorking,
  })),
});

// Check if a staff member works on a given JS Date, returns { isWorking, startTime, endTime }
// Odstepstwo na konkretna date bije grafik tygodniowy — ta sama zasada, co po
// stronie API (booking.service: resolveStaffWindow). Dzieki temu wyszarzone
// sloty w kolumnie same odzwierciedlaja wyjatek, bez osobnej warstwy.
const getStaffWorkingInfo = (
  staff: StaffMember,
  date: Date,
  exception?: StaffScheduleException,
) => {
  if (exception) {
    if (exception.isOff || !exception.startTime || !exception.endTime) {
      return { isWorking: false, startHour: 0, startMin: 0, endHour: 0, endMin: 0 };
    }
    const [xsh, xsm] = exception.startTime.split(':').map(Number);
    const [xeh, xem] = exception.endTime.split(':').map(Number);
    return { isWorking: true, startHour: xsh, startMin: xsm, endHour: xeh, endMin: xem };
  }

  // JS: 0=Sun, 1=Mon... same as schema
  const dow = date.getDay();
  const schedule = staff.schedules.find((sc) => sc.dayOfWeek === dow);
  if (!schedule || !schedule.isWorking) return { isWorking: false, startHour: 0, startMin: 0, endHour: 0, endMin: 0 };
  const [sh, sm] = schedule.startTime.split(':').map(Number);
  const [eh, em] = schedule.endTime.split(':').map(Number);
  return { isWorking: true, startHour: sh, startMin: sm, endHour: eh, endMin: em };
};

// ─── CONSTANTS ──────────────────────────────────────────
const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7:00 - 19:00
// Wysokosc godziny w siatce. Cala geometria (kafle, przeciaganie, linia
// „teraz”) liczy sie od tej stalej — 72 px miesci dwie linie tekstu juz
// w wizycie 45-minutowej.
const HOUR_HEIGHT = 72;
const TIME_COL_WIDTH = 60;
const DAY_COL_MIN_WIDTH = 140;
const WEEK_COL_MIN_WIDTH = 100;
const DAY_NAMES_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'];

const statusConfig: Record<string, { bg: string; text: string; dot: string; label: string }> = {
  CONFIRMED: { ...PREMIUM_COLORS.status.confirmed, label: 'Potwierdzone' },
  PENDING: { ...PREMIUM_COLORS.status.pending, label: 'Oczekujące' },
  IN_PROGRESS: { ...PREMIUM_COLORS.status.inProgress, label: 'W trakcie' },
  COMPLETED: { ...PREMIUM_COLORS.status.completed, label: 'Zakończone' },
  CANCELLED: { ...PREMIUM_COLORS.status.cancelled, label: 'Anulowane' },
  NO_SHOW: { ...PREMIUM_COLORS.status.noShow, label: 'Klient nie przyszedł' },
};

const pad2 = (n: number) => String(n).padStart(2, '0');
const eventTimeRange = (e: CalendarEvent) => `${pad2(e.startHour)}:${pad2(e.startMin)}–${pad2(e.endHour)}:${pad2(e.endMin)}`;

// Pierwsza litera dat z Intl jest mala („sobota, 12 września”).
const firstUpperSx = { '&::first-letter': { textTransform: 'uppercase' } } as const;

// Wyszarzenie godzin poza grafikiem — spokojne tlo bez wzoru.
const blockedSlotSx = { bgcolor: alpha(tokens.surfaceStrong, 0.75) } as const;

// Nieobecnosc — ten sam odcien z delikatnymi paskami, zeby odroznic ja
// od zwyklych godzin poza grafikiem.
const absenceOverlaySx = {
  bgcolor: alpha(tokens.surfaceStrong, 0.7),
  backgroundImage: `repeating-linear-gradient(135deg, transparent 0 7px, ${alpha(tokens.muted, 0.09)} 7px 8px)`,
} as const;

/** Mala etykieta na siatce (nieobecnosc, zmiana godzin w grafiku). */
const GridBadge: React.FC<{ label: string; icon?: React.ReactNode; tone?: 'neutral' | 'plum' }> = ({ label, icon, tone = 'neutral' }) => (
  <Box
    component="span"
    title={label}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      maxWidth: 'calc(100% - 12px)',
      px: 1,
      py: 0.25,
      borderRadius: 999,
      bgcolor: tone === 'plum' ? tokens.plumSoft : tokens.paper,
      border: `1px solid ${tone === 'plum' ? alpha(tokens.plum, 0.2) : tokens.lineStrong}`,
      color: tone === 'plum' ? tokens.plum : tokens.inkSoft,
      fontSize: '0.75rem',
      fontWeight: 650,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
      '& .MuiSvgIcon-root': { fontSize: 14, flexShrink: 0 },
    }}
  >
    {icon}
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
  </Box>
);

/** Naglowek sekcji w panelu bocznym. */
const PanelHeading: React.FC<{ children: React.ReactNode; action?: React.ReactNode }> = ({ children, action }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1, minHeight: 28 }}>
    <Typography component="h2" sx={{ fontSize: '0.9375rem', fontWeight: 700, color: tokens.ink }}>{children}</Typography>
    {action}
  </Stack>
);

// ─── MINI CALENDAR ──────────────────────────────────────
const MiniCalendar: React.FC<{
  selectedDate: Date;
  onDateChange: (d: Date) => void;
  events: CalendarEvent[];
}> = ({ selectedDate, onDateChange, events }) => {
  const [viewMonth, setViewMonth] = useState(new Date(selectedDate));
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = lastDay.getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const isToday = (d: number) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  const isSelected = (d: number) => d === selectedDate.getDate() && month === selectedDate.getMonth() && year === selectedDate.getFullYear();

  // Count events per day for dot indicators
  const eventCountByDay = useMemo(() => {
    const map: Record<number, number> = {};
    events.forEach((e) => {
      const ed = new Date(e.date);
      if (ed.getMonth() === month && ed.getFullYear() === year) {
        map[ed.getDate()] = (map[ed.getDate()] || 0) + 1;
      }
    });
    return map;
  }, [events, month, year]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
        <Typography component="h2" sx={{ fontWeight: 700, fontSize: '0.9375rem', ...firstUpperSx }}>
          {formatMonthYear(viewMonth)}
        </Typography>
        <Stack direction="row">
          <IconButton size="small" aria-label="Poprzedni miesiąc" onClick={() => setViewMonth(new Date(year, month - 1, 1))}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Następny miesiąc" onClick={() => setViewMonth(new Date(year, month + 1, 1))}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', rowGap: 0.25 }}>
        {DAY_NAMES_SHORT.map((d) => (
          <Typography key={d} align="center" sx={{ fontSize: '0.75rem', fontWeight: 600, color: tokens.muted, py: 0.5 }}>
            {d}
          </Typography>
        ))}
        {cells.map((day, i) => {
          if (!day) return <Box key={i} sx={{ height: 32 }} />;
          const selected = isSelected(day);
          const current = isToday(day);
          return (
            <Box
              key={i}
              component="button"
              type="button"
              aria-label={formatDateLong(new Date(year, month, day))}
              aria-pressed={selected}
              onClick={() => onDateChange(new Date(year, month, day))}
              sx={{
                width: 32,
                height: 32,
                mx: 'auto',
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: current && !selected ? `1.5px solid ${tokens.plum}` : '1.5px solid transparent',
                borderRadius: '50%',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: '0.8125rem',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: selected || current ? 700 : 500,
                color: selected ? tokens.paper : current ? tokens.plum : tokens.ink,
                bgcolor: selected ? tokens.plum : 'transparent',
                position: 'relative',
                transition: 'background-color .15s ease',
                '&:hover': { bgcolor: selected ? tokens.plumDark : tokens.plumSoft },
                '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 1 },
              }}
            >
              {day}
              {eventCountByDay[day] && !selected && (
                <Box sx={{
                  position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
                  width: 4, height: 4, borderRadius: '50%', bgcolor: tokens.plumLight,
                }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Wizyty zachodzace na siebie w jednej kolumnie (inni pracownicy w widoku
// tygodnia albo swiadomie zapisana kolizja) dostaja osobne tory, zeby kafle
// stanely obok siebie zamiast przykrywac sobie nawzajem tekst.
type LaneInfo = { lane: number; lanes: number };
const computeLanes = (events: CalendarEvent[]): Record<string, LaneInfo> => {
  const startOf = (e: CalendarEvent) => e.startHour * 60 + e.startMin;
  const endOf = (e: CalendarEvent) => e.endHour * 60 + e.endMin;
  const sorted = [...events].sort((a, b) => startOf(a) - startOf(b) || endOf(b) - endOf(a));
  const result: Record<string, LaneInfo> = {};
  let cluster: CalendarEvent[] = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    cluster.forEach((e) => { result[e.id].lanes = laneEnds.length; });
    cluster = [];
    laneEnds = [];
    clusterEnd = -1;
  };
  sorted.forEach((e) => {
    const start = startOf(e);
    const end = endOf(e);
    if (cluster.length && start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    result[e.id] = { lane, lanes: 1 };
    cluster.push(e);
    clusterEnd = Math.max(clusterEnd, end);
  });
  flush();
  return result;
};

// ─── EVENT CARD ─────────────────────────────────────────
const EventCard: React.FC<{
  event: CalendarEvent;
  staff: StaffMember;
  onClick: (event: CalendarEvent) => void;
  startHourOffset: number;
  /**
   * Podane tylko w widoku dnia — wtedy kafel mozna chwycic i przeciagnac.
   * Klikniecie obsluguje wowczas logika przeciagania (puszczenie bez ruchu),
   * zeby jedno nie wyzwalalo drugiego.
   */
  onMoveStart?: (event: CalendarEvent, e: React.MouseEvent) => void;
  /** Przesuniecie w minutach pokazywane w trakcie przeciagania. */
  moveOffsetMin?: number;
  isMoving?: boolean;
  /** Tor w grupie zachodzacych wizyt (patrz computeLanes). */
  laneInfo?: LaneInfo;
}> = ({ event, staff, onClick, startHourOffset, onMoveStart, moveOffsetMin = 0, isMoving = false, laneInfo }) => {
  const lanes = !isMoving && laneInfo && laneInfo.lanes > 1 ? laneInfo : null;
  const top = (event.startHour - startHourOffset) * HOUR_HEIGHT + (event.startMin / 60) * HOUR_HEIGHT;
  const duration = (event.endHour - event.startHour) * 60 + (event.endMin - event.startMin);
  const height = (duration / 60) * HOUR_HEIGHT;
  const isCancelled = event.status === 'CANCELLED' || event.status === 'NO_SHOW';
  const isCompleted = event.status === 'COMPLETED';
  // Kafel krotkiej uslugi (do ~30 min) nie miesci drugiej linii tekstu.
  // W takim wypadku nazwa uslugi idzie w te sama linie co nazwa klienta,
  // zeby nigdy nie znikala z kalendarza.
  const isCompact = height <= 36;
  const accent = isCancelled ? PREMIUM_COLORS.status.cancelled.dot : staff.color;
  const timeRange = eventTimeRange(event);

  const clientSx = {
    fontSize: '0.8125rem',
    fontWeight: 600,
    lineHeight: 1.25,
    color: isCancelled ? tokens.muted : tokens.ink,
    textDecoration: isCancelled ? 'line-through' : 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  } as const;
  const lineSx = {
    fontSize: '0.75rem',
    lineHeight: 1.25,
    color: tokens.inkSoft,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  } as const;

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`${event.clientName}, ${event.service}, ${timeRange}`}
      onClick={onMoveStart ? undefined : (e) => { e.stopPropagation(); onClick(event); }}
      onMouseDown={onMoveStart ? (e) => { if (e.button === 0) onMoveStart(event, e); } : undefined}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(event); } }}
      sx={{
        position: 'absolute',
        top: top + (moveOffsetMin / 60) * HOUR_HEIGHT,
        left: lanes ? `calc(${(lanes.lane / lanes.lanes) * 100}% + 2px)` : 3,
        right: lanes ? 'auto' : 3,
        width: lanes ? `calc(${100 / lanes.lanes}% - 4px)` : 'auto',
        height: Math.max(height - 2, 24),
        bgcolor: isCancelled ? tokens.surface : isCompleted ? tokens.surfaceStrong : alpha(staff.color, 0.1),
        borderLeft: `3px solid ${isCompleted ? alpha(staff.color, 0.55) : accent}`,
        borderRadius: '4px 8px 8px 4px',
        px: 0.875,
        py: isCompact ? 0 : 0.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: isCompact ? 'center' : 'flex-start',
        gap: '1px',
        // W widoku dnia kafel jest chwytalny, w tygodniowym tylko klikalny.
        cursor: onMoveStart ? (isMoving ? 'grabbing' : 'grab') : 'pointer',
        userSelect: 'none',
        overflow: 'hidden',
        opacity: isCancelled ? 0.8 : 1,
        transition: 'background-color .15s ease, box-shadow .15s ease',
        ...(isMoving && {
          zIndex: 20,
          boxShadow: `0 8px 24px ${alpha(tokens.ink, 0.22)}`,
        }),
        '&:hover': {
          bgcolor: isCancelled ? tokens.surfaceStrong : isCompleted ? alpha(tokens.muted, 0.16) : alpha(staff.color, 0.18),
          zIndex: 10,
          boxShadow: `0 4px 12px ${alpha(tokens.ink, 0.1)}`,
        },
        '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 1, zIndex: 10 },
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
        {isCompleted && <CheckCircleOutlineIcon sx={{ fontSize: 14, color: tokens.success, flexShrink: 0 }} />}
        <Typography component="span" sx={{ ...clientSx, flexShrink: 0, maxWidth: isCompact ? '60%' : '100%' }}>
          {event.clientName}
        </Typography>
        {isCompact && (
          <Typography component="span" sx={lineSx}>
            {event.service}
          </Typography>
        )}
      </Stack>
      {!isCompact && (
        <Typography component="span" sx={lineSx}>
          {event.service}
        </Typography>
      )}
      {height > 58 && (
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ ...lineSx, color: tokens.muted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {timeRange}
          </Typography>
          <Typography component="span" sx={{ ...lineSx, color: tokens.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(event.price)}
          </Typography>
          <Box
            component="span"
            aria-label={event.paid ? 'Opłacona' : 'Nieopłacona'}
            title={event.paid ? 'Opłacona' : 'Nieopłacona'}
            sx={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, bgcolor: event.paid ? PREMIUM_COLORS.status.paid.dot : PREMIUM_COLORS.status.unpaid.dot }}
          />
        </Stack>
      )}
      {height > 84 && event.status !== 'CONFIRMED' && (
        <Box sx={{ mt: 0.25 }}>
          <BookingStatusPill status={event.status} size="small" />
        </Box>
      )}
    </Box>
  );
};

// ─── DIALOG HELPERS ─────────────────────────────────────
/** Tytul okna z przyciskiem zamkniecia. */
const DialogHeader: React.FC<{ children: React.ReactNode; onClose: () => void; id?: string }> = ({ children, onClose, id }) => (
  <DialogTitle id={id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 2 }}>
    <Box component="span" sx={{ minWidth: 0 }}>{children}</Box>
    <IconButton onClick={onClose} size="small" aria-label="Zamknij" sx={{ mr: -0.5 }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>
);

/** Wiersz informacji w szczegolach wizyty: ikona, tresc glowna, dopisek. */
const InfoRow: React.FC<{ icon: React.ReactNode; primary: React.ReactNode; secondary?: React.ReactNode; sx?: SxProps<Theme> }> = ({ icon, primary, secondary, sx }) => (
  <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ ...((sx as object) || {}) }}>
    <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: tokens.surfaceStrong, color: tokens.muted, display: 'grid', placeItems: 'center', flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 18 } }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, pt: secondary ? 0 : 0.5 }}>
      <Typography component="div" sx={{ fontWeight: 650, fontSize: '0.9375rem', lineHeight: 1.4 }}>{primary}</Typography>
      {secondary && <Typography component="div" sx={{ fontSize: '0.8125rem', color: tokens.muted, lineHeight: 1.45 }}>{secondary}</Typography>}
    </Box>
  </Stack>
);

const fieldLabelSx = { fontSize: '0.875rem', fontWeight: 600, color: tokens.ink, mb: 0.75, display: 'block' } as const;

// Zaznaczony chip wyboru (metoda platnosci, wariant). Domyslny hover MUI dla
// kolorowego chipa daje ciemne tlo pod ciemnym tekstem — tu zostaje miekkie tlo.
const choiceChipSx = (active: boolean, tone: 'success' | 'plum') => {
  const base = tone === 'success' ? tokens.success : tokens.plum;
  return {
    fontWeight: 600,
    ...(active && {
      '&.MuiChip-clickable:hover, &.MuiChip-clickable.Mui-focusVisible': { bgcolor: alpha(base, 0.18) },
      '& .MuiChip-icon': { color: base },
    }),
  };
};

// ─── EVENT DETAIL DIALOG ────────────────────────────────
const EventDetailDialog: React.FC<{
  event: CalendarEvent | null;
  staff: StaffMember | undefined;
  open: boolean;
  onClose: () => void;
  onStatusChange: (id: string, status: CalendarEvent['status']) => void;
  onSetPayment: (id: string, method: PaymentMethod | null) => void;
  onFinalize: (id: string, amount: number, method: PaymentMethod, extras: ExtraItem[]) => void;
  onDelete: (id: string) => void;
  onEdit: (event: CalendarEvent) => void;
  services: ServiceOption[];
  products: ProductOption[];
  paymentMethods: { value: PaymentMethod; label: string }[];
}> = ({
  event, staff, open, onClose, onStatusChange, onSetPayment, onFinalize, onDelete, onEdit,
  services, products, paymentMethods,
}) => {
  // Panel finalizacji: koszyk + kwota + metoda płatności
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod | null>(null);
  const [extras, setExtras] = useState<ExtraItem[]>([]);
  const confirm = useConfirm();

  useEffect(() => {
    if (event && open) {
      setFinalizeOpen(false);
      setPayAmount(String(event.price ?? ''));
      setPayMethod(event.paymentMethod ?? null);
      setExtras([]);
    }
  }, [event, open]);

  const addExtraService = (svc: ServiceOption) =>
    setExtras((prev) => [
      ...prev,
      { key: `s:${svc.id}:${Date.now()}`, serviceId: svc.id, name: svc.name, quantity: 1, unitPrice: svc.price },
    ]);

  const addExtraProduct = (prod: ProductOption) =>
    setExtras((prev) => [
      ...prev,
      { key: `p:${prod.id}:${Date.now()}`, productId: prod.id, name: prod.name, quantity: 1, unitPrice: prod.price },
    ]);

  const extrasTotal = extras.reduce((sum, e) => sum + e.quantity * e.unitPrice, 0);
  const grandTotal = (Number(payAmount) || 0) + extrasTotal;

  if (!event || !staff) return null;
  const isCompleted = event.status === 'COMPLETED';
  const isCancelled = event.status === 'CANCELLED';
  const isTerminal = isCompleted || isCancelled;
  const leftActive = isCompleted || (finalizeOpen && !isTerminal);
  const rightActive = isCancelled;
  const timeStr = eventTimeRange(event);
  const duration = (event.endHour - event.startHour) * 60 + (event.endMin - event.startMin);
  const eventDate = new Date(`${event.date}T12:00:00`);

  // Dane wizyty pokazywane w potwierdzeniu anulowania i usuniecia.
  const confirmDetails = (
    <Stack gap={0.25}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{event.clientName}</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>{event.service}</Typography>
      <Typography component="div" sx={{ fontSize: '0.875rem', color: tokens.inkSoft, fontVariantNumeric: 'tabular-nums', ...firstUpperSx }}>
        {formatWeekdayDay(eventDate)}, {timeStr}
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>Pracownik: {staff.name}</Typography>
    </Stack>
  );

  const handleCancelClick = async () => {
    if (isTerminal) return;
    const ok = await confirm({
      title: 'Anulować tę wizytę?',
      details: confirmDetails,
      description: 'Termin zwolni się w kalendarzu.',
      confirmLabel: 'Anuluj wizytę',
      cancelLabel: 'Zostaw wizytę',
      tone: 'danger',
    });
    if (ok) {
      setFinalizeOpen(false);
      onStatusChange(event.id, 'CANCELLED');
    }
  };

  const handleDeleteClick = async () => {
    const ok = await confirm({
      title: 'Usunąć wizytę z kalendarza?',
      details: confirmDetails,
      description: 'Wizyta zostanie oznaczona jako anulowana, a termin zwolni się w kalendarzu.',
      confirmLabel: 'Usuń wizytę',
      cancelLabel: 'Zostaw wizytę',
      tone: 'danger',
    });
    if (ok) onDelete(event.id);
  };

  const segmentSx = (active: boolean) => ({
    flex: 1,
    zIndex: 1,
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    px: 1,
    cursor: isTerminal ? 'default' : 'pointer',
    fontFamily: 'inherit',
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: active ? tokens.paper : tokens.inkSoft,
    transition: 'color .25s ease',
    borderRadius: 999,
    '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 },
    '& .MuiSvgIcon-root': { fontSize: 20 },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="event-detail-title">
      <DialogHeader id="event-detail-title" onClose={onClose}>Szczegóły wizyty</DialogHeader>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <BookingStatusPill status={event.status} />
        </Box>

        <Stack gap={1.75}>
          <InfoRow icon={<ContentCutIcon />} primary={event.service} secondary={formatDuration(duration)} />
          <InfoRow
            icon={<AccessTimeIcon />}
            primary={<Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{timeStr}</Box>}
            secondary={<Box component="span" sx={{ display: 'inline-block', ...firstUpperSx }}>{formatWeekdayDay(eventDate)}</Box>}
          />
          <InfoRow icon={<PersonOutlineIcon />} primary={event.clientName} secondary={event.clientPhone || undefined} />
          <Stack direction="row" gap={1.5} alignItems="center">
            <PersonAvatar firstName={staff.firstName} lastName={staff.lastName} seed={staff.id} size={32} sx={{ bgcolor: staff.color }} />
            <Box>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 650, lineHeight: 1.4 }}>{staff.name}</Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, lineHeight: 1.45 }}>Pracownik</Typography>
            </Box>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Price */}
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: tokens.surfaceStrong, color: tokens.muted, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <PaymentIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 750, fontSize: '1.125rem', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(event.price)}</Typography>
          <StatusPill label={event.paid ? 'Opłacona' : 'Nieopłacona'} tone={event.paid ? 'success' : 'warning'} size="small" />
        </Stack>

        {/* Payment method — po finalizacji można skorygować metodę */}
        {isCompleted && (
          <Box sx={{ mt: 2 }}>
            <Typography component="span" sx={fieldLabelSx}>Metoda płatności</Typography>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {paymentMethods.map((m) => {
                const active = event.paid && event.paymentMethod === m.value;
                return (
                  <Chip
                    key={m.value}
                    label={m.label}
                    // Re-clicking the active method clears it (marks unpaid)
                    onClick={() => onSetPayment(event.id, active ? null : m.value)}
                    color={active ? 'success' : 'default'}
                    variant={active ? 'filled' : 'outlined'}
                    icon={active ? <CheckIcon /> : undefined}
                    aria-pressed={active}
                    sx={choiceChipSx(active, 'success')}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {event.notes && (
          <Box sx={{ mt: 2, px: 1.75, py: 1.5, borderRadius: '10px', bgcolor: tokens.surface, border: `1px solid ${tokens.line}` }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mb: 0.25 }}>Notatka</Typography>
            <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, whiteSpace: 'pre-wrap' }}>{event.notes}</Typography>
          </Box>
        )}

        {/* Finalizacja — przełącznik dwóch stanów */}
        <Typography component="span" sx={{ ...fieldLabelSx, mt: 2.5 }}>Finalizacja wizyty</Typography>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            height: 52,
            borderRadius: 999,
            border: `1px solid ${tokens.line}`,
            bgcolor: tokens.surfaceStrong,
            overflow: 'hidden',
          }}
        >
          {/* Przesuwający się wskaźnik */}
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              bottom: 4,
              width: 'calc(50% - 8px)',
              borderRadius: 999,
              transition: 'left .3s cubic-bezier(.4,0,.2,1), opacity .3s ease, background-color .3s ease',
              left: leftActive ? 4 : 'calc(50% + 4px)',
              opacity: leftActive || rightActive ? 1 : 0,
              bgcolor: leftActive ? tokens.success : tokens.danger,
            }}
          />
          <Box
            component="button"
            type="button"
            aria-pressed={leftActive}
            onClick={() => { if (!isTerminal) setFinalizeOpen(true); }}
            sx={segmentSx(leftActive)}
          >
            <CheckCircleOutlineIcon />
            {isCompleted ? 'Sfinalizowana' : 'Sfinalizuj'}
          </Box>
          <Box
            component="button"
            type="button"
            aria-pressed={rightActive}
            onClick={handleCancelClick}
            sx={segmentSx(rightActive)}
          >
            <CloseIcon />
            {isCancelled ? 'Anulowana' : 'Anuluj'}
          </Box>
        </Box>

        {/* Po przełączeniu na „Sfinalizuj": kwota + metoda płatności */}
        <Collapse in={finalizeOpen && !isTerminal}>
          <Box sx={{ p: 2, mt: 1.5, borderRadius: '12px', bgcolor: tokens.surface, border: `1px solid ${tokens.line}` }}>
            <Stack gap={2}>
              <TextField
                label="Kwota do zapłaty"
                type="number"
                fullWidth
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">zł</InputAdornment> }}
                helperText="Cena wizyty — możesz ją skorygować przed zamknięciem."
              />
              {/* Dosprzedaż przy kasie (uwagi klienta 16 i 19) */}
              <Box>
                <Typography component="span" sx={fieldLabelSx}>Dosprzedaż</Typography>
                <Stack gap={1}>
                  <Autocomplete
                    fullWidth
                    options={services}
                    getOptionLabel={(o) => `${o.name} — ${formatMoney(o.price, { decimals: 2 })}`}
                    value={null}
                    blurOnSelect
                    onChange={(_, v) => v && addExtraService(v)}
                    renderInput={(params) => <TextField {...params} placeholder="Dodaj usługę" inputProps={{ ...params.inputProps, 'aria-label': 'Dodaj usługę' }} />}
                    noOptionsText="Brak usług"
                  />
                  <Autocomplete
                    fullWidth
                    options={products}
                    getOptionLabel={(o) => `${o.name} — ${formatMoney(o.price, { decimals: 2 })}`}
                    value={null}
                    blurOnSelect
                    onChange={(_, v) => v && addExtraProduct(v)}
                    renderInput={(params) => <TextField {...params} placeholder="Dodaj produkt" inputProps={{ ...params.inputProps, 'aria-label': 'Dodaj produkt' }} />}
                    noOptionsText="Brak produktów z ceną sprzedaży"
                  />
                </Stack>
                {extras.length > 0 && (
                  <Stack divider={<Divider flexItem />} sx={{ mt: 1.25, border: `1px solid ${tokens.line}`, borderRadius: '10px', bgcolor: tokens.paper }}>
                    {extras.map((e) => (
                      <Box key={e.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 1.5, pr: 0.5, py: 0.5 }}>
                        <Typography sx={{ flexGrow: 1, minWidth: 0, fontSize: '0.875rem' }}>
                          {e.name}
                          {e.quantity > 1 && (
                            <Box component="span" sx={{ color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>{` × ${e.quantity}`}</Box>
                          )}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Dodaj kolejną sztukę: ${e.name}`}
                          onClick={() =>
                            setExtras((prev) =>
                              prev.map((x) => (x.key === e.key ? { ...x, quantity: x.quantity + 1 } : x)),
                            )
                          }
                        >
                          <AddIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, minWidth: 76, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {formatMoney(e.quantity * e.unitPrice, { decimals: 2 })}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label={`Usuń pozycję: ${e.name}`}
                          onClick={() => setExtras((prev) => prev.filter((x) => x.key !== e.key))}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography component="span" sx={fieldLabelSx}>Metoda płatności</Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {paymentMethods.map((m) => {
                    const active = payMethod === m.value;
                    return (
                      <Chip
                        key={m.value}
                        label={m.label}
                        onClick={() => setPayMethod(active ? null : m.value)}
                        color={active ? 'success' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        icon={active ? <CheckIcon /> : undefined}
                        aria-pressed={active}
                        sx={choiceChipSx(active, 'success')}
                      />
                    );
                  })}
                </Stack>
              </Box>
              <Button
                fullWidth
                variant="contained"
                color="success"
                disabled={!payMethod || !(grandTotal > 0)}
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() =>
                  onFinalize(event.id, Number(payAmount), payMethod as PaymentMethod, extras)
                }
              >
                Potwierdź finalizację{grandTotal > 0 ? ` — ${formatMoney(grandTotal, { decimals: 2 })}` : ''}
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={handleDeleteClick}
        >
          Usuń
        </Button>
        <Button
          variant="outlined"
          startIcon={<EditOutlinedIcon />}
          onClick={() => onEdit(event)}
          disabled={isTerminal}
        >
          Edytuj
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── ABSENCE DIALOG ─────────────────────────────────────
// Uwaga klienta 14: w kalendarzu trzeba moc zaznaczyc cos innego niz wizyta
// klienta — chorobe, urlop, wyjscie. Korzystamy z istniejacego modulu
// nieobecnosci; wpis dodany przez wlasciciela jest od razu zatwierdzony.
const ABSENCE_RANGE_ERROR = 'Data zakończenia nie może być wcześniejsza niż rozpoczęcia.';

const AbsenceDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  staff: StaffMember[];
  defaultDate: string;
  onSaved: () => void;
}> = ({ open, onClose, staff, defaultDate, onSaved }) => {
  const [staffId, setStaffId] = useState('');
  const [type, setType] = useState<Absence['type']>('SICK_LEAVE');
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStaffId(staff[0]?.id || '');
    setType('SICK_LEAVE');
    setStartDate(defaultDate);
    setEndDate(defaultDate);
    setNote('');
    setError('');
  }, [open, defaultDate, staff]);

  const handleSave = async () => {
    if (endDate < startDate) {
      setError(ABSENCE_RANGE_ERROR);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await absenceApi.create({ staffId, type, startDate, endDate, ...(note ? { note } : {}) });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Nie udało się zapisać nieobecności');
    } finally {
      setSaving(false);
    }
  };

  // Blad zakresu dat pokazujemy przy polu „Do”, na biezaco.
  const rangeInvalid = Boolean(startDate && endDate && endDate < startDate);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="absence-dialog-title">
      <DialogHeader id="absence-dialog-title" onClose={onClose}>Dodaj nieobecność</DialogHeader>
      <DialogContent>
        <Typography sx={{ color: tokens.inkSoft, fontSize: '0.9375rem', mb: 2 }}>
          Choroba, urlop lub wyjście pracownika. Wizyty w tym czasie nadal można dodać — kalendarz tylko o tym ostrzeże.
        </Typography>
        {error && error !== ABSENCE_RANGE_ERROR && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          <TextField
            label="Pracownik"
            select
            fullWidth
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
          >
            {staff.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Powód"
            select
            fullWidth
            value={type}
            onChange={(e) => setType(e.target.value as Absence['type'])}
          >
            {(Object.keys(ABSENCE_LABELS) as Absence['type'][]).map((t) => (
              <MenuItem key={t} value={t}>{ABSENCE_LABELS[t]}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Od"
            type="date"
            fullWidth
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            label="Do"
            type="date"
            fullWidth
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={rangeInvalid}
            helperText={rangeInvalid ? ABSENCE_RANGE_ERROR : 'Ostatni dzień nieobecności.'}
            sx={{ minWidth: 170 }}
          />
          <TextField
            label="Notatka (opcjonalnie)"
            fullWidth
            multiline
            minRows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !staffId || !startDate || !endDate}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Zapisywanie…' : 'Dodaj nieobecność'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── EDIT BOOKING DIALOG ────────────────────────────────
// Okno „Edytuj" ze szczegolow wizyty. Pozwala poprawic termin, pracownika
// i notatke. Zmiana uslugi celowo nie jest tu dostepna — pociaga za soba
// inny czas trwania i cene, wiec czeka na przebudowe modelu wizyty.
const EditBookingDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  staff: StaffMember[];
  onSave: (values: { date: string; startTime: string; staffId: string; notes: string }) => Promise<string | null>;
}> = ({ open, onClose, event, staff, onSave }) => {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [staffId, setStaffId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !event) return;
    setDate(event.date);
    setStartTime(
      `${String(event.startHour).padStart(2, '0')}:${String(event.startMin).padStart(2, '0')}`,
    );
    setStaffId(event.staffId);
    setNotes(event.notes || '');
    setError('');
  }, [open, event]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const err = await onSave({ date, startTime, staffId, notes });
    setSaving(false);
    // Gdy backend poprosil o potwierdzenie, decyzja zapada w osobnym oknie —
    // tutaj nie pokazujemy bledu i zostawiamy formularz nietkniety.
    if (err && err !== PENDING_CONFIRMATION) setError(err);
  };

  if (!event) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="edit-booking-title">
      <DialogHeader id="edit-booking-title" onClose={onClose}>Edytuj wizytę</DialogHeader>
      <DialogContent>
        <Box sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>{event.clientName}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>
            {event.service} · <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{eventTimeRange(event)}</Box>
          </Typography>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
          <TextField
            label="Data"
            type="date"
            fullWidth
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField
            label="Godzina rozpoczęcia"
            type="time"
            fullWidth
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 900 }}
          />
          <TextField
            label="Pracownik"
            select
            fullWidth
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          >
            {staff.map((m) => (
              <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Notatka (opcjonalnie)"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ gridColumn: '1 / -1' }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !date || !startTime || !staffId}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── NEW BOOKING DIALOG ─────────────────────────────────
const NewBookingDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  // Zwraca null przy sukcesie lub komunikat błędu — formularz nie może się resetować po błędzie.
  onSave: (event: CalendarEvent) => Promise<string | null>;
  staff: StaffMember[];
  services: ServiceOption[];
  clients: ClientOption[];
  salonId: string | null;
  onClientCreated: (client: ClientOption) => void;
  prefill?: { date: string; staffId: string; startHour: number; startMin: number; endHour?: number; endMin?: number };
}> = ({ open, onClose, onSave, staff, services, clients, salonId, onClientCreated, prefill }) => {
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(null);
  // Wariant uslugi (uwaga klienta 8). Gdy usluga ma warianty, to on decyduje
  // o cenie i czasie wizyty — sama usluga podaje tylko wartosc orientacyjna.
  const [selectedVariant, setSelectedVariant] =
    useState<{ id: string; name: string; price: number; duration: number } | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<string>(prefill?.staffId || staff[0]?.id || '');
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [clientInput, setClientInput] = useState('');
  const [date, setDate] = useState(prefill?.date || toKey(new Date()));
  const [startTime, setStartTime] = useState(
    prefill ? `${String(prefill.startHour).padStart(2, '0')}:${String(prefill.startMin).padStart(2, '0')}` : '09:00'
  );
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Inline "add client" sub-dialog (opens on top of this dialog, never closes it)
  const [addClientOpen, setAddClientOpen] = useState(false);
  // "Nie podal" to potwierdzenie recepcji, ze brak jest swiadomy, a nie dana
  // do zapisania — do API idzie po prostu pusta wartosc (uwaga klienta 5).
  const [addClientForm, setAddClientForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', noEmail: false, noPhone: false,
  });
  const [addClientLoading, setAddClientLoading] = useState(false);
  const [addClientError, setAddClientError] = useState('');

  const openAddClient = (typedName: string) => {
    const parts = typedName.trim().split(/\s+/);
    setAddClientForm({
      firstName: parts[0] || '',
      lastName: parts.slice(1).join(' '),
      email: '',
      phone: '',
      noEmail: false,
      noPhone: false,
    });
    setAddClientError('');
    setAddClientOpen(true);
  };

  const handleCreateClient = async () => {
    if (!salonId) { setAddClientError('Nie wybrano salonu.'); return; }
    setAddClientLoading(true);
    setAddClientError('');
    try {
      const input: CreateClientInput = {
        salonId,
        firstName: addClientForm.firstName.trim(),
        lastName: addClientForm.lastName.trim(),
        ...(addClientForm.email.trim() ? { email: addClientForm.email.trim() } : {}),
        ...(addClientForm.phone.trim() ? { phone: addClientForm.phone.trim() } : {}),
      };
      const created = await clientApi.create(input);
      const option: ClientOption = {
        id: created.id,
        name: `${created.firstName} ${created.lastName}`,
        phone: created.phone || '',
      };
      onClientCreated(option);
      setSelectedClient(option);
      setClientInput(option.name);
      setAddClientOpen(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.errors?.[0]?.msg ||
        'Nie udało się dodać klienta.';
      setAddClientError(msg);
    } finally {
      setAddClientLoading(false);
    }
  };

  // Reset when prefill changes
  React.useEffect(() => {
    if (prefill) {
      setDate(prefill.date);
      setSelectedStaff(prefill.staffId);
      setStartTime(`${String(prefill.startHour).padStart(2, '0')}:${String(prefill.startMin).padStart(2, '0')}`);
    }
  }, [prefill]);

  // Stary komunikat błędu nie może wisieć po ponownym otwarciu dialogu;
  // przy otwarciu bez wybranego pracownika domyślnie zaznacz pierwszego widocznego.
  React.useEffect(() => {
    if (open) {
      setSaveError('');
      if (!selectedStaff) {
        const first = staff.find((s) => s.visible);
        if (first) setSelectedStaff(first.id);
      }
    }
  }, [open]);

  const handleSave = async () => {
    if (!selectedService || !selectedClient || !selectedStaff) return;
    // Usluga z wariantami wymaga wyboru — inaczej nie wiadomo, ile trwa i kosztuje.
    if (selectedService.variants?.length && !selectedVariant) return;
    const effective = selectedVariant ?? selectedService;
    const [h, m] = startTime.split(':').map(Number);
    const endTotalMin = h * 60 + m + effective.duration;
    const newEvt: CalendarEvent = {
      id: `new-${Date.now()}`,
      date,
      startHour: h,
      startMin: m,
      endHour: Math.floor(endTotalMin / 60),
      endMin: endTotalMin % 60,
      service: selectedVariant
        ? `${selectedService.name} — ${selectedVariant.name}`
        : selectedService.name,
      serviceId: selectedService.id,
      serviceVariantId: selectedVariant?.id,
      clientName: selectedClient.name,
      clientPhone: selectedClient.phone,
      clientId: selectedClient.id,
      staffId: selectedStaff,
      status: 'CONFIRMED',
      price: effective.price,
      paid: false,
      notes: notes || undefined,
    };
    setSaving(true);
    setSaveError('');
    const error = await onSave(newEvt);
    setSaving(false);
    if (error === PENDING_CONFIRMATION) {
      // Pytanie „czy na pewno?" wisi nad tym dialogiem — zostawiamy dane.
      return;
    }
    if (error) {
      // Błąd zapisu: formularz zostaje nietknięty, żeby użytkownik mógł poprawić dane.
      setSaveError(error);
      return;
    }
    // Reset dopiero po udanym zapisie
    setSelectedService(null);
    setSelectedClient(null);
    setClientInput('');
    setNotes('');
  };

  const visibleServiceStaff = staff.filter((s) => s.visible && (!selectedService || s.serviceIds.includes(selectedService.id)));

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="new-booking-title">
      <DialogHeader id="new-booking-title" onClose={onClose}>Nowa wizyta</DialogHeader>
      <DialogContent>
        <Stack spacing={2.25}>
          {saveError && <Alert severity="error" onClose={() => setSaveError('')}>{saveError}</Alert>}
          {/* Client */}
          <Autocomplete
            options={clients}
            getOptionLabel={(o) => o.name}
            value={selectedClient}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => {
              if (v && v.id === ADD_CLIENT_SENTINEL_ID) {
                openAddClient(v.name);
                return;
              }
              setSelectedClient(v);
            }}
            inputValue={clientInput}
            onInputChange={(_, v) => setClientInput(v)}
            filterOptions={(options, params) => {
              const filtered = clientFilterOptions(options, params);
              const trimmed = params.inputValue.trim();
              filtered.push({ id: ADD_CLIENT_SENTINEL_ID, name: trimmed, phone: '' });
              return filtered;
            }}
            renderInput={(params) => (
              <TextField {...params} label="Klient" placeholder="Wyszukaj lub dodaj klienta" />
            )}
            renderOption={(props, option) => {
              if (option.id === ADD_CLIENT_SENTINEL_ID) {
                return (
                  <li {...props} key={ADD_CLIENT_SENTINEL_ID}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ color: tokens.plum }}>
                      <AddIcon fontSize="small" />
                      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 650 }}>
                        {option.name ? `Dodaj nowego klienta: „${option.name}”` : 'Dodaj nowego klienta'}
                      </Typography>
                    </Stack>
                  </li>
                );
              }
              return (
                <li {...props} key={option.id}>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{option.name}</Typography>
                    {option.phone && <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{option.phone}</Typography>}
                  </Stack>
                </li>
              );
            }}
          />

          {/* Service */}
          <Autocomplete
            options={services}
            getOptionLabel={(o) => `${o.name} · ${formatDuration(o.duration)} · ${formatMoney(o.price)}`}
            value={selectedService}
            onChange={(_, v) => {
              setSelectedService(v);
              // Wariant nalezy do uslugi — zmiana uslugi uniewaznia wybor.
              setSelectedVariant(v?.variants?.length === 1 ? v.variants[0] : null);
              // Jeśli aktualnie wybrany pracownik nie świadczy nowej usługi,
              // przełącz na pierwszego widocznego, który ją świadczy — inaczej
              // ukryty wybór poleciałby do API i skończył się błędem walidacji.
              if (v) {
                const current = staff.find((s) => s.id === selectedStaff);
                if (!current || !current.serviceIds.includes(v.id)) {
                  const firstAvailable = staff.find((s) => s.visible && s.serviceIds.includes(v.id));
                  setSelectedStaff(firstAvailable?.id || '');
                }
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Usługa" placeholder="Wybierz usługę" />
            )}
            noOptionsText="Brak usług o tej nazwie"
          />

          {/* Wariant usługi (uwaga klienta 8) */}
          {selectedService?.variants && selectedService.variants.length > 0 && (
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 0.75 }}>Wariant usługi</FormLabel>
              <Stack direction="row" gap={0.75} flexWrap="wrap">
                {selectedService.variants.map((v) => {
                  const active = selectedVariant?.id === v.id;
                  return (
                    <Chip
                      key={v.id}
                      label={`${v.name} · ${formatMoney(v.price)}`}
                      onClick={() => setSelectedVariant(v)}
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      icon={active ? <CheckIcon /> : undefined}
                      aria-pressed={active}
                      sx={choiceChipSx(active, 'plum')}
                    />
                  );
                })}
              </Stack>
              {!selectedVariant && <FormHelperText>Wybierz wariant — od niego zależy cena i czas wizyty.</FormHelperText>}
            </FormControl>
          )}

          {/* Staff */}
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 0.75 }}>Pracownik</FormLabel>
            <Stack direction="row" gap={0.75} flexWrap="wrap">
              {visibleServiceStaff.map((s) => {
                const isSelected = selectedStaff === s.id;
                return (
                  <Chip
                    key={s.id}
                    avatar={
                      <Avatar sx={{ bgcolor: `${s.color} !important`, color: `${tokens.paper} !important`, fontWeight: 700 }}>
                        {isSelected ? <CheckIcon sx={{ fontSize: 16 }} /> : s.avatar}
                      </Avatar>
                    }
                    label={s.name}
                    variant="outlined"
                    aria-pressed={isSelected}
                    onClick={() => setSelectedStaff(s.id)}
                    sx={{
                      fontWeight: isSelected ? 700 : 600,
                      color: tokens.ink,
                      bgcolor: isSelected ? `${alpha(s.color, 0.1)} !important` : tokens.paper,
                      borderColor: isSelected ? s.color : tokens.lineStrong,
                      '& .MuiChip-avatar': { width: 24, height: 24, fontSize: '0.75rem', ml: 0.5 },
                    }}
                  />
                );
              })}
            </Stack>
            {selectedService && visibleServiceStaff.length === 0 && (
              <FormHelperText error>
                Żaden z widocznych pracowników nie świadczy tej usługi.
              </FormHelperText>
            )}
          </FormControl>

          {/* Date & Time */}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <TextField
              type="date"
              label="Data"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              type="time"
              label="Godzina"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
              helperText={prefill?.endHour != null
                ? `Zaznaczono ${pad2(prefill.startHour)}:${pad2(prefill.startMin)}–${pad2(prefill.endHour)}:${pad2(prefill.endMin ?? 0)}. Długość dopasuje się do usługi.`
                : undefined}
            />
          </Box>

          {/* Duration & price preview */}
          {selectedService && (
            <Box sx={{ px: 2, py: 1.5, borderRadius: '12px', bgcolor: tokens.surface, border: `1px solid ${tokens.line}` }}>
              {/* Uwaga klienta 6: sam czas trwania nie wystarcza — recepcja
                  chce widziec konkretny przedzial godzin. */}
              {[
                {
                  label: 'Godziny',
                  value: (() => {
                    const [h, m] = startTime.split(':').map(Number);
                    if (Number.isNaN(h) || Number.isNaN(m)) return '—';
                    const end = h * 60 + m + (selectedVariant ?? selectedService).duration;
                    const hh = String(Math.floor(end / 60) % 24).padStart(2, '0');
                    const mm = String(end % 60).padStart(2, '0');
                    return `${startTime}–${hh}:${mm}`;
                  })(),
                  strong: true,
                },
                {
                  label: 'Czas trwania',
                  value: selectedVariant
                    ? formatDuration(selectedVariant.duration)
                    : selectedService.durationMax != null
                      ? `${formatDuration(selectedService.duration)} – ${formatDuration(selectedService.durationMax)}`
                      : formatDuration(selectedService.duration),
                },
                {
                  label: 'Cena',
                  value: selectedVariant
                    ? formatMoney(selectedVariant.price)
                    : selectedService.priceMax != null
                      ? `${formatNumber(selectedService.price)}–${formatMoney(selectedService.priceMax)}`
                      : formatMoney(selectedService.price),
                  strong: true,
                },
              ].map((row, i) => (
                <Stack key={row.label} direction="row" justifyContent="space-between" gap={2} sx={{ mt: i ? 0.5 : 0 }}>
                  <Typography sx={{ fontSize: '0.875rem', color: tokens.muted }}>{row.label}</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: row.strong ? 700 : 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                    {row.value}
                  </Typography>
                </Stack>
              ))}
            </Box>
          )}

          {/* Notes */}
          <TextField
            label="Notatka (opcjonalnie)"
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Np. preferencje klienta, alergie"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            !selectedService ||
            !selectedClient ||
            !selectedStaff ||
            saving ||
            Boolean(selectedService.variants?.length && !selectedVariant)
          }
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Zapisywanie…' : 'Umów wizytę'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Inline add-client dialog — layered above the booking dialog, which stays open */}
    <Dialog open={addClientOpen} onClose={() => setAddClientOpen(false)} maxWidth="xs" fullWidth aria-labelledby="add-client-title">
      <DialogHeader id="add-client-title" onClose={() => setAddClientOpen(false)}>Dodaj klienta</DialogHeader>
      <DialogContent>
        {addClientError && <Alert severity="error" sx={{ mb: 2 }}>{addClientError}</Alert>}
        <Stack spacing={2}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <TextField
              label="Imię"
              fullWidth
              required
              value={addClientForm.firstName}
              onChange={(e) => setAddClientForm({ ...addClientForm, firstName: e.target.value })}
            />
            <TextField
              label="Nazwisko"
              fullWidth
              required
              value={addClientForm.lastName}
              onChange={(e) => setAddClientForm({ ...addClientForm, lastName: e.target.value })}
            />
          </Box>
          <Box>
            <TextField
              label="Adres e-mail"
              type="email"
              fullWidth
              disabled={addClientForm.noEmail}
              value={addClientForm.email}
              onChange={(e) => setAddClientForm({ ...addClientForm, email: e.target.value })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={addClientForm.noEmail}
                  onChange={(e) =>
                    setAddClientForm({ ...addClientForm, noEmail: e.target.checked, email: '' })
                  }
                />
              }
              label={<Typography sx={{ fontSize: '0.875rem' }}>Klient nie podał e-maila</Typography>}
              sx={{ mt: 0.25 }}
            />
          </Box>
          <Box>
            <TextField
              label="Telefon"
              fullWidth
              disabled={addClientForm.noPhone}
              value={addClientForm.phone}
              onChange={(e) => setAddClientForm({ ...addClientForm, phone: e.target.value })}
              placeholder="+48 600 000 000"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={addClientForm.noPhone}
                  onChange={(e) =>
                    setAddClientForm({ ...addClientForm, noPhone: e.target.checked, phone: '' })
                  }
                />
              }
              label={<Typography sx={{ fontSize: '0.875rem' }}>Klient nie podał telefonu</Typography>}
              sx={{ mt: 0.25 }}
            />
          </Box>
          <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted }}>
            Uzupełnij e-mail i telefon albo zaznacz, że klient ich nie podał.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddClientOpen(false)}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={handleCreateClient}
          disabled={
            addClientLoading ||
            !addClientForm.firstName.trim() ||
            !addClientForm.lastName.trim() ||
            !(addClientForm.email.trim() || addClientForm.noEmail) ||
            !(addClientForm.phone.trim() || addClientForm.noPhone)
          }
          startIcon={addClientLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {addClientLoading ? 'Dodawanie…' : 'Dodaj klienta'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

// ─── MAIN CALENDAR PAGE ─────────────────────────────────
export const SalonCalendarPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Panel z minikalendarzem, podsumowaniem i filtrami — na waskich ekranach
  // chowany nad siatka, zeby kalendarz byl od razu widoczny.
  const [panelOpen, setPanelOpen] = useState(false);
  const isWide = useMediaQuery((t: Theme) => t.breakpoints.up('lg'), { noSsr: true });
  const navigate = useNavigate();
  const [paymentFilters, setPaymentFilters] = useState({ paid: false, unpaid: false });
  const [statusFilters, setStatusFilters] = useState<Record<string, boolean>>({
    CONFIRMED: false, PENDING: false, IN_PROGRESS: false, COMPLETED: false, CANCELLED: false, NO_SHOW: false,
  });
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [allEvents, setAllEvents] = useState<CalendarEvent[]>([]);
  const [salonId, setSalonId] = useState<string | null>(null);
  const [apiServices, setApiServices] = useState<ServiceOption[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  // Metody wlaczone w salonie (uwaga klienta 17). Domyslnie wszystkie, zeby
  // salon bez zapisanej konfiguracji dzialal jak dotad.
  const [activePaymentMethods, setActivePaymentMethods] =
    useState<{ value: PaymentMethod; label: string }[]>(PAYMENT_METHODS);
  const [apiClients, setApiClients] = useState<ClientOption[]>([]);
  const [loading, setLoading] = useState(true);
  const { activeSalonId, loadSalons } = useSalonContextStore();

  useEffect(() => {
    loadSalons();
  }, [loadSalons]);

  // ─── Fetch salon + staff + services + clients on mount ───
  useEffect(() => {
    if (!activeSalonId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        if (cancelled) return;
        setSalonId(activeSalonId);

        const [staffResult, serviceResult, clientResult, hoursResult] = await Promise.allSettled([
          staffApi.listBySalon(activeSalonId),
          serviceApi.listBySalon(activeSalonId),
          clientApi.listBySalon(activeSalonId, 1, 100),
          salonApi.getWorkingHours(activeSalonId),
        ]);

        if (cancelled) return;

        // A branch with zero working-hours rows is "not configured" — surface a
        // friendly banner instead of letting booking creation hard-fail.
        setNoWorkingHours(
          hoursResult.status === 'fulfilled' && Array.isArray(hoursResult.value) && hoursResult.value.length === 0,
        );

        if (staffResult.status === 'fulfilled') {
          const staffList = Array.isArray(staffResult.value) ? staffResult.value : (staffResult.value as any).data || [];
          let savedVisibility: Record<string, boolean> = {};
          try {
            const key = getStaffVisibilityStorageKey(activeSalonId);
            const raw = localStorage.getItem(key);
            savedVisibility = raw ? JSON.parse(raw) : {};
          } catch {
            savedVisibility = {};
          }

          const staffColors = assignStaffColors(staffList);
          setStaffMembers(staffList.map((staff: Staff) => {
            const member = staffToMember(staff, staffColors[staff.id]);
            const visible = savedVisibility[staff.id];
            return visible === undefined ? member : { ...member, visible };
          }));
        } else {
          console.error('Failed to load staff:', staffResult.reason);
        }

        if (serviceResult.status === 'fulfilled') {
          const serviceList = Array.isArray(serviceResult.value) ? serviceResult.value : (serviceResult.value as any).data || [];
          setApiServices(serviceList.map((s: Service) => ({
            id: s.id,
            name: s.name,
            duration: s.duration,
            price: Number(s.price),
            durationMax: s.durationMax ?? null,
            priceMax: s.priceMax != null ? Number(s.priceMax) : null,
            variants: (s.variants || []).map((v) => ({
              id: v.id, name: v.name, price: Number(v.price), duration: v.duration,
            })),
          })));
        } else {
          console.error('Failed to load services:', serviceResult.reason);
        }

        // Produkty do dosprzedazy przy finalizacji. Osobno od reszty, bo salon
        // bez magazynu ma dzialac tak samo — brak produktow to nie blad.
        salonApi
          .getById(activeSalonId)
          .then((salon) => {
            const enabled = enabledPaymentMethods(salon);
            setActivePaymentMethods(PAYMENT_METHODS.filter((m) => enabled.includes(m.value)));
          })
          .catch(() => setActivePaymentMethods(PAYMENT_METHODS));

        inventoryApiService
          .getProducts({ salonId: activeSalonId, includeBrand: true })
          .then((list) =>
            setProducts(
              list
                .filter((p) => p.isActive && Number(p.sellingPrice ?? 0) > 0)
                .map((p) => ({ id: p.id, name: p.name, unit: p.unit, price: Number(p.sellingPrice) })),
            ),
          )
          .catch(() => setProducts([]));

        if (clientResult.status === 'fulfilled') {
          const clientRes = clientResult.value;
          const clientList = Array.isArray(clientRes) ? clientRes : (clientRes as any).data || [];
          setApiClients(clientList.map((c: Client) => ({
            id: c.id, name: `${c.firstName} ${c.lastName}`, phone: c.phone || '',
          })));
        } else {
          console.error('Failed to load clients:', clientResult.reason);
        }
      } catch (err) {
        console.error('Failed to load calendar data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSalonId]);

  // ─── Fetch bookings when salonId or selectedDate changes ───
  useEffect(() => {
    if (!salonId) return;
    let cancelled = false;
    (async () => {
      try {
        const monday = new Date(selectedDate);
        const day = (monday.getDay() + 6) % 7;
        monday.setDate(monday.getDate() - day);
        const sunday = new Date(monday);
        sunday.setDate(sunday.getDate() + 6);

        const res = await bookingApi.list({
          salonId,
          dateFrom: toKey(monday),
          dateTo: toKey(sunday),
          limit: 100,
        });
        if (cancelled) return;
        const bookings = Array.isArray(res) ? res : res.data || [];
        setAllEvents(bookings.map(bookingToEvent));
      } catch (err) {
        console.error('Failed to load bookings:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [salonId, selectedDate]);

  // ─── Persist staff visibility per salon/user ───
  useEffect(() => {
    if (!salonId || staffMembers.length === 0) return;
    try {
      const key = getStaffVisibilityStorageKey(salonId);
      const value = Object.fromEntries(staffMembers.map((s) => [s.id, s.visible]));
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore localStorage errors silently.
    }
  }, [salonId, staffMembers]);

  // Dialogs
  const [detailEvent, setDetailEvent] = useState<CalendarEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [newBookingPrefill, setNewBookingPrefill] = useState<{ date: string; staffId: string; startHour: number; startMin: number; endHour?: number; endMin?: number } | undefined>();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [noWorkingHours, setNoWorkingHours] = useState(false);
  const [hoursEditorOpen, setHoursEditorOpen] = useState(false);
  // Pracownik, ktorego grafik edytujemy z poziomu kalendarza (klikniecie w naglowek kolumny).
  const [scheduleStaff, setScheduleStaff] = useState<{ id: string; name: string } | null>(null);
  // Operacja odlozona do potwierdzenia: backend zwrocil ostrzezenia,
  // uzytkownik decyduje, czy wykonac ja mimo nich. Wspolne dla zakladania
  // wizyty i dla przenoszenia jej w kalendarzu — okno jest jedno.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    warnings: BookingWarning[];
    run: () => Promise<string | null>;
  } | null>(null);
  const [confirmingAction, setConfirmingAction] = useState(false);
  // Zapis grafiku dopuszcza tylko SALON_OWNER i SUPER_ADMIN (staff.routes.ts),
  // wiec pracownikowi nie pokazujemy edytora — inaczej dostalby 403 przy zapisie.
  const currentUserRole = useAuthStore((s) => s.user?.role);
  const canEditSchedules = currentUserRole === 'SALON_OWNER' || currentUserRole === 'SUPER_ADMIN';
  // Typ nieobecnosci bywa wrazliwy (L4), wiec endpoint jest dostepny tylko dla
  // wlasciciela i kierownictwa — zwykly pracownik nie zobaczy tych blokow.
  const canSeeAbsences =
    currentUserRole === 'SALON_OWNER' ||
    currentUserRole === 'SUPER_ADMIN' ||
    currentUserRole === 'MANAGER';
  const [absences, setAbsences] = useState<Absence[]>([]);
  const [scheduleExceptions, setScheduleExceptions] = useState<StaffScheduleException[]>([]);
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);

  const refetchWorkingHours = useCallback(async () => {
    if (!activeSalonId) return;
    try {
      const hrs = await salonApi.getWorkingHours(activeSalonId);
      setNoWorkingHours(Array.isArray(hrs) && hrs.length === 0);
    } catch {
      /* keep current state on transient error */
    }
  }, [activeSalonId]);

  const gridRef = useRef<HTMLDivElement>(null);

  // ─── Date helpers ───
  // Po zapisaniu grafiku odswiezamy same harmonogramy, zachowujac ustawiona
  // widocznosc i kolory juz zaladowanych pracownikow.
  const refreshStaffSchedules = useCallback(async () => {
    if (!salonId) return;
    try {
      const res = await staffApi.listBySalon(salonId);
      const list: Staff[] = Array.isArray(res) ? res : (res as any).data || [];
      setStaffMembers((prev) =>
        prev.map((m) => {
          const fresh = list.find((s) => s.id === m.id);
          if (!fresh) return m;
          return {
            ...m,
            schedules: (fresh.schedules || []).map((sc) => ({
              dayOfWeek: sc.dayOfWeek,
              startTime: sc.startTime,
              endTime: sc.endTime,
              isWorking: sc.isWorking,
            })),
          };
        }),
      );
    } catch (err) {
      console.error('Failed to refresh staff schedules:', err);
    }
  }, [salonId]);

  const getMonday = useCallback((d: Date) => {
    const result = new Date(d);
    const day = (result.getDay() + 6) % 7;
    result.setDate(result.getDate() - day);
    return result;
  }, []);

  const weekDays = useMemo(() => {
    const mon = getMonday(selectedDate);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [selectedDate, getMonday]);

  // ─── Filter events ───
  const filteredEvents = useMemo(() => {
    const visibleStaffIds = new Set(staffMembers.filter((s) => s.visible).map((s) => s.id));
    return allEvents.filter((e) => {
      if (!visibleStaffIds.has(e.staffId)) return false;
      if (paymentFilters.paid && !e.paid) return false;
      if (paymentFilters.unpaid && e.paid) return false;
      const activeStatuses = Object.entries(statusFilters).filter(([, v]) => v).map(([k]) => k);
      if (activeStatuses.length > 0 && !activeStatuses.includes(e.status)) return false;
      return true;
    });
  }, [allEvents, paymentFilters, statusFilters, staffMembers]);

  // Events for current day/week
  const dayKey = toKey(selectedDate);
  const weekKeys = new Set(weekDays.map(toKey));
  const visibleEvents = viewMode === 'day'
    ? filteredEvents.filter((e) => e.date === dayKey)
    : filteredEvents.filter((e) => weekKeys.has(e.date));

  // ─── Date navigation ───
  const changeDate = (offset: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + (viewMode === 'week' ? offset * 7 : offset));
    setSelectedDate(d);
  };

  // ─── Now line ───
  const now = new Date();
  const nowPosition = (now.getHours() - 7) * HOUR_HEIGHT + (now.getMinutes() / 60) * HOUR_HEIGHT;
  const showNowLine = now.getHours() >= 7 && now.getHours() < 20;

  // ─── Slot click → open new booking ───
  const handleSlotClick = useCallback((staffId: string, date: string, hour: number, isHalf: boolean) => {
    setNewBookingPrefill({ date, staffId, startHour: hour, startMin: isHalf ? 30 : 0 });
    setNewBookingOpen(true);
  }, []);

  // ─── Click-and-drag range selection (day view) ───
  // slotIndex: half-hour index from HOURS[0] (07:00). 0 = 07:00, 1 = 07:30, …
  const SLOT_MIN = 30;
  const GRID_START_MIN = HOURS[0] * 60;
  const [drag, setDrag] = useState<{ staffId: string; start: number; end: number } | null>(null);

  const beginDrag = useCallback((staffId: string, slotIndex: number) => {
    setDrag({ staffId, start: slotIndex, end: slotIndex });
  }, []);
  const extendDrag = useCallback((staffId: string, slotIndex: number) => {
    setDrag((d) => (d && d.staffId === staffId ? { ...d, end: slotIndex } : d));
  }, []);

  const finalizeDrag = useCallback(() => {
    setDrag((prev) => {
      if (!prev) return null;
      const lo = Math.min(prev.start, prev.end);
      const hi = Math.max(prev.start, prev.end);
      const startTotal = GRID_START_MIN + lo * SLOT_MIN;
      const endTotal = GRID_START_MIN + (hi + 1) * SLOT_MIN;
      setNewBookingPrefill({
        date: toKey(selectedDate),
        staffId: prev.staffId,
        startHour: Math.floor(startTotal / 60),
        startMin: startTotal % 60,
        endHour: Math.floor(endTotal / 60),
        endMin: endTotal % 60,
      });
      setNewBookingOpen(true);
      return null;
    });
  }, [selectedDate, GRID_START_MIN]);

  useEffect(() => {
    const onUp = () => finalizeDrag();
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [finalizeDrag]);

  // ─── Event actions ───
  const handleEventClick = (event: CalendarEvent) => {
    setDetailEvent(event);
    setDetailOpen(true);
  };

  const refreshBookings = useCallback(async () => {
    if (!salonId) return;
    try {
      const monday = new Date(selectedDate);
      const day = (monday.getDay() + 6) % 7;
      monday.setDate(monday.getDate() - day);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const res = await bookingApi.list({ salonId, dateFrom: toKey(monday), dateTo: toKey(sunday), limit: 100 });
      const bookings = Array.isArray(res) ? res : res.data || [];
      setAllEvents(bookings.map(bookingToEvent));
    } catch (err) {
      console.error('Failed to refresh bookings:', err);
    }
  }, [salonId, selectedDate]);

  const refreshAbsences = useCallback(async () => {
    if (!salonId || !canSeeAbsences) {
      setAbsences([]);
      return;
    }
    try {
      const monday = new Date(selectedDate);
      const day = (monday.getDay() + 6) % 7;
      monday.setDate(monday.getDate() - day);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      setAbsences(await absenceApi.calendar({ salonId, from: toKey(monday), to: toKey(sunday) }));
    } catch (err) {
      // Brak nieobecnosci nie moze psuc kalendarza — logujemy i idziemy dalej.
      console.error('Failed to load absences:', err);
      setAbsences([]);
    }
  }, [salonId, selectedDate, canSeeAbsences]);

  useEffect(() => { refreshAbsences(); }, [refreshAbsences]);

  // Wyjatki w grafikach pracownikow na widoczny tydzien (uwaga klienta 3).
  // Endpoint jest per pracownik, wiec pytamy rownolegle — w salonie jest ich
  // kilku, a dzieki temu nie trzeba osobnej trasy na poziomie salonu.
  const refreshScheduleExceptions = useCallback(async () => {
    if (staffMembers.length === 0) {
      setScheduleExceptions([]);
      return;
    }
    try {
      const monday = new Date(selectedDate);
      const day = (monday.getDay() + 6) % 7;
      monday.setDate(monday.getDate() - day);
      const sunday = new Date(monday);
      sunday.setDate(sunday.getDate() + 6);
      const perStaff = await Promise.all(
        staffMembers.map((s) =>
          staffApi
            .getScheduleExceptions(s.id, { from: toKey(monday), to: toKey(sunday) })
            .catch(() => [] as StaffScheduleException[]),
        ),
      );
      setScheduleExceptions(perStaff.flat());
    } catch (err) {
      // Jak przy nieobecnosciach: brak wyjatkow nie moze psuc kalendarza.
      console.error('Failed to load schedule exceptions:', err);
      setScheduleExceptions([]);
    }
  }, [staffMembers, selectedDate]);

  useEffect(() => { refreshScheduleExceptions(); }, [refreshScheduleExceptions]);

  // Wejscie z powiadomienia: ?date=YYYY-MM-DD&bookingId=... Kalendarz nie ma
  // trasy do pojedynczej wizyty, wiec ustawiamy dzien i otwieramy jej
  // szczegoly, gdy tylko kafle sie wczytaja.
  useEffect(() => {
    const date = searchParams.get('date');
    if (!date) return;
    const parsed = new Date(`${date}T12:00:00`);
    if (!Number.isNaN(parsed.getTime())) setSelectedDate(parsed);
    // Parametr daty zuzywamy od razu; bookingId zostaje do kolejnego przebiegu,
    // bo wizyty moga jeszcze nie byc wczytane.
    const bookingId = searchParams.get('bookingId');
    setSearchParams(bookingId ? { bookingId } : {}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    if (!bookingId || allEvents.length === 0) return;
    const found = allEvents.find((e) => e.id === bookingId);
    if (!found) return;
    setDetailEvent(found);
    setDetailOpen(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, allEvents, setSearchParams]);

  const findScheduleException = useCallback(
    (staffId: string, date: Date): StaffScheduleException | undefined => {
      const key = toKey(date);
      return scheduleExceptions.find(
        (e) => e.staffId === staffId && e.date.slice(0, 10) === key,
      );
    },
    [scheduleExceptions],
  );

  // Nieobecnosc danego pracownika obejmujaca wskazany dzien (jesli jest).
  const findAbsence = useCallback(
    (staffId: string, date: Date): Absence | undefined => {
      const key = toKey(date);
      return absences.find(
        (a) =>
          a.staff?.id === staffId &&
          a.startDate.slice(0, 10) <= key &&
          a.endDate.slice(0, 10) >= key,
      );
    },
    [absences],
  );

  // ─── Przeciaganie wizyty (uwaga klienta 12) ───
  // Kafel chwytamy lewym przyciskiem, przesuwamy w pionie (godzina, skok
  // 15 minut) i w poziomie (inny pracownik). Puszczenie bez ruchu = zwykle
  // klikniecie, wiec podglad szczegolow dziala jak dotad.
  const MOVE_STEP_MIN = 15;
  const [moveDrag, setMoveDrag] = useState<{
    eventId: string;
    deltaMin: number;
    targetStaffId: string;
  } | null>(null);
  const moveDragRef = useRef<{
    event: CalendarEvent;
    originY: number;
    deltaMin: number;
    targetStaffId: string;
    moved: boolean;
  } | null>(null);

  const applyEventMove = useCallback(
    async (
      event: CalendarEvent,
      deltaMin: number,
      targetStaffId: string,
      force: boolean,
    ): Promise<string | null> => {
      const startTotal = event.startHour * 60 + event.startMin + deltaMin;
      if (startTotal < 0) return 'Nie można przenieść wizyty poza godziny widoczne w kalendarzu.';
      const startTime = `${String(Math.floor(startTotal / 60)).padStart(2, '0')}:${String(startTotal % 60).padStart(2, '0')}`;

      try {
        await bookingApi.update(event.id, {
          date: event.date,
          startTime,
          staffId: targetStaffId,
          ...(force ? { force: true } : {}),
        });
        setPendingConfirm(null);
        setSnackbar({ open: true, message: `Wizytę przeniesiono na ${startTime}`, severity: 'success' });
        refreshBookings();
        return null;
      } catch (err: any) {
        const data = err?.response?.data;
        if (data?.code === 'BOOKING_NEEDS_CONFIRMATION' && Array.isArray(data?.details?.warnings)) {
          setPendingConfirm({
            title: 'Czy na pewno przenieść tę wizytę?',
            warnings: data.details.warnings as BookingWarning[],
            run: () => applyEventMove(event, deltaMin, targetStaffId, true),
          });
          return PENDING_CONFIRMATION;
        }
        console.error('Failed to reschedule booking:', err);
        return data?.error || 'Nie udało się przenieść wizyty';
      }
    },
    [refreshBookings],
  );

  const beginEventMove = useCallback((event: CalendarEvent, e: React.MouseEvent) => {
    // Bez tego pod kaflem ruszyloby zaznaczanie nowego zakresu godzin.
    e.stopPropagation();
    e.preventDefault();
    moveDragRef.current = {
      event,
      originY: e.clientY,
      deltaMin: 0,
      targetStaffId: event.staffId,
      moved: false,
    };
    setMoveDrag({ eventId: event.id, deltaMin: 0, targetStaffId: event.staffId });
  }, []);

  const isMovingEvent = moveDrag !== null;

  // Edycja wizyty z okna szczegolow (przycisk „Edytuj").
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null);

  const applyEventEdit = useCallback(
    async (
      event: CalendarEvent,
      values: { date: string; startTime: string; staffId: string; notes: string },
      force: boolean,
    ): Promise<string | null> => {
      try {
        await bookingApi.update(event.id, {
          date: values.date,
          startTime: values.startTime,
          staffId: values.staffId,
          notes: values.notes,
          ...(force ? { force: true } : {}),
        });
        setPendingConfirm(null);
        setEditEvent(null);
        setDetailOpen(false);
        setSnackbar({ open: true, message: 'Zapisano zmiany w wizycie', severity: 'success' });
        refreshBookings();
        return null;
      } catch (err: any) {
        const data = err?.response?.data;
        if (data?.code === 'BOOKING_NEEDS_CONFIRMATION' && Array.isArray(data?.details?.warnings)) {
          setPendingConfirm({
            title: 'Czy na pewno zapisać te zmiany?',
            warnings: data.details.warnings as BookingWarning[],
            run: () => applyEventEdit(event, values, true),
          });
          return PENDING_CONFIRMATION;
        }
        console.error('Failed to update booking:', err);
        return data?.error || 'Nie udało się zapisać zmian';
      }
    },
    [refreshBookings],
  );

  useEffect(() => {
    if (!isMovingEvent) return;

    const onMove = (e: MouseEvent) => {
      const st = moveDragRef.current;
      if (!st) return;
      const rawMin = ((e.clientY - st.originY) / HOUR_HEIGHT) * 60;
      const deltaMin = Math.round(rawMin / MOVE_STEP_MIN) * MOVE_STEP_MIN;

      // Kolumna pod kursorem wyznacza docelowego pracownika.
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const col = el?.closest('[data-staff-column]') as HTMLElement | null;
      const targetStaffId = col?.dataset.staffColumn || st.targetStaffId;

      if (deltaMin !== st.deltaMin || targetStaffId !== st.targetStaffId) {
        st.deltaMin = deltaMin;
        st.targetStaffId = targetStaffId;
        if (deltaMin !== 0 || targetStaffId !== st.event.staffId) st.moved = true;
        setMoveDrag({ eventId: st.event.id, deltaMin, targetStaffId });
      }
    };

    const onUp = async () => {
      const st = moveDragRef.current;
      moveDragRef.current = null;
      setMoveDrag(null);
      if (!st) return;

      if (!st.moved) {
        // Puszczono bez ruchu — to bylo zwykle klikniecie.
        handleEventClick(st.event);
        return;
      }

      const error = await applyEventMove(st.event, st.deltaMin, st.targetStaffId, false);
      if (error && error !== PENDING_CONFIRMATION) {
        setSnackbar({ open: true, message: error, severity: 'error' });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // Zalezymy tylko od momentu rozpoczecia/zakonczenia przeciagania —
    // reszta stanu czytana jest z refa, wiec nasluch nie przepina sie
    // przy kazdym ruchu myszy.
  }, [isMovingEvent]);

  const handleStatusChange = async (id: string, status: CalendarEvent['status']) => {
    try {
      await bookingApi.updateStatus(id, status);
      setAllEvents((prev) => prev.map((e) => e.id === id ? { ...e, status } : e));
      setDetailOpen(false);
      setSnackbar({ open: true, message: `Status zmieniony na: ${statusConfig[status].label}`, severity: 'success' });
    } catch (err: any) {
      console.error('Failed to update booking status:', err);
      const msg = err?.response?.data?.error || 'Nie udało się zmienić statusu';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  // Finalizacja wizyty: status COMPLETED + płatność (kwota i metoda) w jednym kroku
  const handleFinalize = async (
    id: string,
    amount: number,
    method: PaymentMethod,
    extras: ExtraItem[],
  ) => {
    try {
      // Jedno zadanie zamiast dwoch: status, platnosc i dosprzedaz w jednej
      // transakcji po stronie API. Wczesniej dalo sie skonczyc z wizyta
      // zamknieta, ale bez zapisanej platnosci.
      const { total } = await bookingApi.finalize(id, {
        paymentMethod: method,
        amount,
        ...(extras.length > 0
          ? {
              extras: extras.map((e) => ({
                ...(e.serviceId ? { serviceId: e.serviceId } : {}),
                ...(e.productId ? { productId: e.productId } : {}),
                name: e.name,
                quantity: e.quantity,
                unitPrice: e.unitPrice,
              })),
            }
          : {}),
      });
      setAllEvents((prev) => prev.map((e) => (
        e.id === id ? { ...e, status: 'COMPLETED' as const, paid: true, paymentMethod: method, price: amount } : e
      )));
      setDetailOpen(false);
      setSnackbar({ open: true, message: `Wizyta sfinalizowana — ${formatMoney(total, { decimals: 2 })}`, severity: 'success' });
    } catch (err: any) {
      console.error('Failed to finalize booking:', err);
      refreshBookings();
      const msg = err?.response?.data?.error || 'Nie udało się sfinalizować wizyty';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleSetPayment = async (id: string, method: PaymentMethod | null) => {
    // Optimistic UI update — reflect selection immediately, revert on failure
    const patch = { paid: method !== null, paymentMethod: method ?? undefined };
    setAllEvents((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    setDetailEvent((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
    try {
      await bookingApi.setPayment(id, method);
      const label = method ? PAYMENT_METHODS.find((m) => m.value === method)?.label : null;
      setSnackbar({
        open: true,
        message: label ? `Płatność: ${label}` : 'Płatność wyczyszczona',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Failed to set payment:', err);
      refreshBookings();
      const msg = err?.response?.data?.error || 'Nie udało się zapisać płatności';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleDeleteEvent = async (id: string) => {
    try {
      await bookingApi.updateStatus(id, 'CANCELLED');
      setAllEvents((prev) => prev.map((e) => e.id === id ? { ...e, status: 'CANCELLED' as const } : e));
      setDetailOpen(false);
      setSnackbar({ open: true, message: 'Wizyta została anulowana', severity: 'info' });
    } catch (err: any) {
      console.error('Failed to cancel booking:', err);
      const msg = err?.response?.data?.error || 'Nie udało się anulować wizyty';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  // Jedno miejsce zapisu wizyty — uzywane zarowno przy pierwszej probie,
  // jak i po potwierdzeniu ostrzezen (wtedy z force=true).
  const submitBooking = async (event: CalendarEvent, force: boolean): Promise<string | null> => {
    if (!salonId) return 'Nie wybrano salonu.';
    try {
      await bookingApi.create({
        salonId,
        serviceId: event.serviceId,
        ...(event.serviceVariantId ? { serviceVariantId: event.serviceVariantId } : {}),
        staffId: event.staffId,
        date: event.date,
        startTime: `${String(event.startHour).padStart(2, '0')}:${String(event.startMin).padStart(2, '0')}`,
        clientId: event.clientId,
        notes: event.notes,
        ...(force ? { force: true } : {}),
      });
      setPendingConfirm(null);
      setNewBookingOpen(false);
      setNewBookingPrefill(undefined);
      setSnackbar({ open: true, message: `Umówiono wizytę: ${event.service}`, severity: 'success' });
      refreshBookings();
      return null;
    } catch (err: any) {
      const data = err?.response?.data;
      // Backend nie blokuje — prosi o decyzje. Komplet powodow pokazujemy
      // w osobnym oknie zamiast czerwonego bledu w formularzu.
      if (data?.code === 'BOOKING_NEEDS_CONFIRMATION' && Array.isArray(data?.details?.warnings)) {
        setPendingConfirm({
          title: 'Czy na pewno dodać tę wizytę?',
          warnings: data.details.warnings as BookingWarning[],
          run: () => submitBooking(event, true),
        });
        return PENDING_CONFIRMATION;
      }
      console.error('Failed to create booking:', err);
      // Błąd wraca do dialogu (inline alert) — formularz zostaje z danymi użytkownika.
      return data?.error || 'Nie udało się utworzyć wizyty';
    }
  };

  const handleNewBookingSave = (event: CalendarEvent) => submitBooking(event, false);

  const handleConfirmPendingAction = async () => {
    if (!pendingConfirm) return;
    setConfirmingAction(true);
    const error = await pendingConfirm.run();
    setConfirmingAction(false);
    if (error && error !== PENDING_CONFIRMATION) {
      setPendingConfirm(null);
      setSnackbar({ open: true, message: error, severity: 'error' });
    }
  };

  // Staff toggle
  const setStaffVisibility = (id: string, visible: boolean) => {
    setStaffMembers((prev) => {
      const target = prev.find((s) => s.id === id);
      if (!target) return prev;

      const visibleCount = prev.filter((s) => s.visible).length;
      if (!visible && target.visible && visibleCount === 1) {
        return prev;
      }

      return prev.map((s) => (s.id === id ? { ...s, visible } : s));
    });
  };

  const toggleStaffVisibility = (id: string) => {
    const target = staffMembers.find((s) => s.id === id);
    if (!target) return;
    setStaffVisibility(id, !target.visible);
  };

  const showAllStaff = () => {
    setStaffMembers((prev) => prev.map((s) => ({ ...s, visible: true })));
  };

  const visibleStaff = staffMembers.filter((s) => s.visible);
  const visibleStaffCount = visibleStaff.length;

  // ─── Day summary stats ───
  const dayEvents = allEvents.filter((e) => e.date === dayKey);
  const dayRevenue = dayEvents.reduce((sum, e) => sum + e.price, 0);
  const dayCompleted = dayEvents.filter((e) => e.status === 'COMPLETED').length;


  const activeFilterCount =
    Object.values(statusFilters).filter(Boolean).length +
    (paymentFilters.paid ? 1 : 0) +
    (paymentFilters.unpaid ? 1 : 0);
  const hiddenStaffCount = staffMembers.length - visibleStaffCount;

  const clearFilters = () => {
    setPaymentFilters({ paid: false, unpaid: false });
    setStatusFilters({ CONFIRMED: false, PENDING: false, IN_PROGRESS: false, COMPLETED: false, CANCELLED: false, NO_SHOW: false });
  };

  // Header date display
  const weekLabel = (() => {
    const first = weekDays[0];
    const last = weekDays[6];
    if (first.getFullYear() !== last.getFullYear()) {
      return `${formatDayMonth(first)} ${first.getFullYear()} – ${formatDayMonth(last)} ${last.getFullYear()}`;
    }
    if (first.getMonth() !== last.getMonth()) {
      return `${formatDayMonth(first)} – ${formatDayMonth(last)} ${last.getFullYear()}`;
    }
    return `${first.getDate()}–${formatDayMonth(last)} ${last.getFullYear()}`;
  })();
  const headerLabel = viewMode === 'day' ? formatDateLong(selectedDate) : weekLabel;
  const openNewBooking = () => { setNewBookingPrefill(undefined); setNewBookingOpen(true); };
  const newBookingLabel = 'Nowa wizyta';

  const pageTitle = (
    <Typography variant="h1" component="h1">Kalendarz</Typography>
  );

  // ─── RENDER ───
  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 2.5 }}>{pageTitle}</Box>
        <LoadingState variant="block" height={560} />
      </Box>
    );
  }

  // Wiersz etykiet godzin — przyklejony do lewej, gdy siatka przewija sie w bok.
  const timeColumn = (
    <Box
      aria-hidden
      sx={{
        width: TIME_COL_WIDTH,
        flexShrink: 0,
        position: 'sticky',
        left: 0,
        zIndex: 25,
        bgcolor: 'background.paper',
        borderRight: `1px solid ${tokens.line}`,
      }}
    >
      {HOURS.map((h, i) => (
        <Box key={h} sx={{ height: HOUR_HEIGHT, position: 'relative' }}>
          <Typography
            component="span"
            sx={{
              position: 'absolute',
              top: i === 0 ? 4 : -9,
              right: 10,
              fontSize: '0.75rem',
              fontWeight: 600,
              lineHeight: '18px',
              color: tokens.muted,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {pad2(h)}:00
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const slotLineSx = (half: boolean) => ({
    borderTop: `1px ${half ? 'dashed' : 'solid'} ${PREMIUM_COLORS.calendar.slotBorder}`,
  });

  // ─── Panel boczny: minikalendarz, podsumowanie, pracownicy, filtry ───
  const summaryRows = [
    { label: 'Wizyty', value: formatNumber(dayEvents.length) },
    { label: 'Zakończone', value: formatNumber(dayCompleted) },
    { label: 'Przychód', value: formatMoney(dayRevenue) },
  ];

  const panelSections = [
    <MiniCalendar key="mini" selectedDate={selectedDate} onDateChange={setSelectedDate} events={allEvents} />,

    <Box key="summary">
      <PanelHeading>Podsumowanie dnia</PanelHeading>
      <Stack gap={0.75}>
        {summaryRows.map((row) => (
          <Stack key={row.label} direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
            <Typography sx={{ fontSize: '0.875rem', color: tokens.muted }}>{row.label}</Typography>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{row.value}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>,

    <Box key="staff">
      <PanelHeading
        action={hiddenStaffCount > 0 ? (
          <Button size="small" variant="text" onClick={showAllStaff} sx={{ mr: -1 }}>Pokaż wszystkich</Button>
        ) : (
          <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, fontVariantNumeric: 'tabular-nums' }}>
            {visibleStaffCount}/{staffMembers.length}
          </Typography>
        )}
      >
        Pracownicy
      </PanelHeading>
      {staffMembers.length === 0 ? (
        <Typography sx={{ fontSize: '0.875rem', color: tokens.muted }}>Brak pracowników w salonie.</Typography>
      ) : (
        <Stack gap={0.25} sx={{ mx: -0.75 }}>
          {staffMembers.map((s) => (
            <Box
              key={s.id}
              component="button"
              type="button"
              aria-pressed={s.visible}
              aria-label={`${s.visible ? 'Ukryj' : 'Pokaż'} w kalendarzu: ${s.name}`}
              onClick={() => toggleStaffVisibility(s.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                width: '100%',
                border: 'none',
                bgcolor: 'transparent',
                fontFamily: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                py: 0.625,
                px: 0.75,
                borderRadius: '9px',
                color: s.visible ? tokens.ink : tokens.subtle,
                transition: 'background-color .15s ease',
                '&:hover': { bgcolor: tokens.surfaceStrong },
                '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 },
              }}
            >
              <PersonAvatar
                firstName={s.firstName}
                lastName={s.lastName}
                seed={s.id}
                size={28}
                sx={{ bgcolor: s.color, opacity: s.visible ? 1 : 0.4 }}
              />
              <Typography component="span" noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', fontWeight: s.visible ? 600 : 500, color: 'inherit' }}>
                {s.name}
              </Typography>
              {s.visible
                ? <VisibilityIcon sx={{ fontSize: 18, color: tokens.muted }} />
                : <VisibilityOffIcon sx={{ fontSize: 18, color: tokens.subtle }} />}
            </Box>
          ))}
        </Stack>
      )}
    </Box>,

    <Box key="filters">
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: filtersOpen ? 0.5 : 0, minHeight: 28 }}>
        <Box
          component="button"
          type="button"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen(!filtersOpen)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, border: 'none', bgcolor: 'transparent', p: 0,
            fontFamily: 'inherit', cursor: 'pointer', color: tokens.ink, borderRadius: '6px',
            '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: 2 },
          }}
        >
          <Typography component="h2" sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>
            Filtry wizyt{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Typography>
          <ExpandMoreIcon sx={{ fontSize: 20, color: tokens.muted, transition: 'transform .2s ease', transform: filtersOpen ? 'rotate(180deg)' : 'none' }} />
        </Box>
        {activeFilterCount > 0 && (
          <Button size="small" variant="text" onClick={clearFilters} sx={{ mr: -1 }}>Wyczyść</Button>
        )}
      </Stack>
      <Collapse in={filtersOpen}>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mt: 0.5 }}>
          Płatność
        </Typography>
        <FormGroup>
          {[
            { key: 'paid' as const, label: 'Opłacone', dot: PREMIUM_COLORS.status.paid.dot },
            { key: 'unpaid' as const, label: 'Nieopłacone', dot: PREMIUM_COLORS.status.unpaid.dot },
          ].map((f) => (
            <FormControlLabel
              key={f.key}
              control={<Checkbox size="small" checked={paymentFilters[f.key]} onChange={(e) => setPaymentFilters((p) => ({ ...p, [f.key]: e.target.checked }))} sx={{ py: 0.5 }} />}
              label={<Stack direction="row" alignItems="center" gap={0.75}><Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: f.dot }} /><Typography component="span" sx={{ fontSize: '0.875rem' }}>{f.label}</Typography></Stack>}
              sx={{ ml: -0.75, mr: 0 }}
            />
          ))}
        </FormGroup>
        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: tokens.muted, mt: 1 }}>
          Status
        </Typography>
        <FormGroup>
          {Object.entries(statusConfig).map(([key, cfg]) => (
            <FormControlLabel
              key={key}
              control={<Checkbox size="small" checked={statusFilters[key] || false} onChange={(e) => setStatusFilters((s) => ({ ...s, [key]: e.target.checked }))} sx={{ py: 0.5 }} />}
              label={<Stack direction="row" alignItems="center" gap={0.75}><Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.dot }} /><Typography component="span" sx={{ fontSize: '0.875rem' }}>{cfg.label}</Typography></Stack>}
              sx={{ ml: -0.75, mr: 0 }}
            />
          ))}
        </FormGroup>
      </Collapse>
    </Box>,
  ];

  // Liczba aktywnych zawezen: filtry wizyt plus ukryci pracownicy.
  const panelActiveCount = activeFilterCount + (hiddenStaffCount > 0 ? 1 : 0);
  const panelLabel = `Filtry${panelActiveCount > 0 ? ` (${panelActiveCount})` : ''}`;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        // Na duzym ekranie kalendarz wypelnia wysokosc okna i przewija sie
        // w srodku; nizej strona plynie normalnie.
        height: { lg: 'calc(100vh - 140px)' },
        minHeight: { lg: 600 },
      }}
    >
      {/* ─── HEADER ─── */}
      <Box component="header" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', columnGap: 3, rowGap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          {pageTitle}
          <Typography component="div" sx={{ mt: 0.5, color: tokens.inkSoft, fontSize: '0.9375rem', ...firstUpperSx }}>
            {headerLabel}
            <Box component="span" sx={{ color: tokens.muted }}>
              {' · '}{countLabel(visibleEvents.length, 'wizyta', 'wizyty', 'wizyt')}
            </Box>
          </Typography>
        </Box>
        {/* Na telefonie „Nowa wizyta” jest przyciskiem w rogu ekranu, a „Nieobecność” w pasku ponizej. */}
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
          {canSeeAbsences && (
            <Button variant="outlined" startIcon={<EventBusyIcon />} onClick={() => setAbsenceDialogOpen(true)}>
              Nieobecność
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewBooking}>
            {newBookingLabel}
          </Button>
        </Stack>
      </Box>

      {/* ─── TOOLBAR ─── */}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => { if (v) setViewMode(v); }}
          aria-label="Widok kalendarza"
        >
          <ToggleButton value="day">Dzień</ToggleButton>
          <ToggleButton value="week">Tydzień</ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" alignItems="center" gap={0.5}>
          <Tooltip title={viewMode === 'week' ? 'Poprzedni tydzień' : 'Poprzedni dzień'}>
            <IconButton
              onClick={() => changeDate(-1)}
              aria-label={viewMode === 'week' ? 'Poprzedni tydzień' : 'Poprzedni dzień'}
              sx={{ width: 38, height: 38, border: `1px solid ${tokens.lineStrong}`, bgcolor: tokens.paper }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" size="small" onClick={() => setSelectedDate(new Date())} sx={{ minHeight: 38 }}>
            Dziś
          </Button>
          <Tooltip title={viewMode === 'week' ? 'Następny tydzień' : 'Następny dzień'}>
            <IconButton
              onClick={() => changeDate(1)}
              aria-label={viewMode === 'week' ? 'Następny tydzień' : 'Następny dzień'}
              sx={{ width: 38, height: 38, border: `1px solid ${tokens.lineStrong}`, bgcolor: tokens.paper }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {!isWide && (
          <Stack direction="row" gap={1} sx={{ ml: { sm: 'auto' } }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TuneIcon />}
              endIcon={<ExpandMoreIcon sx={{ transition: 'transform .2s ease', transform: panelOpen ? 'rotate(180deg)' : 'none' }} />}
              onClick={() => setPanelOpen((v) => !v)}
              aria-expanded={panelOpen}
              aria-controls="calendar-side-panel"
              sx={{ minHeight: 38 }}
            >
              {panelLabel}
            </Button>
            {canSeeAbsences && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventBusyIcon />}
                onClick={() => setAbsenceDialogOpen(true)}
                sx={{ minHeight: 38, display: { xs: 'inline-flex', sm: 'none' } }}
              >
                Nieobecność
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {/* Empty-state: branch has no configured opening hours */}
      {noWorkingHours && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={() => setHoursEditorOpen(true)}>
              Ustaw godziny otwarcia
            </Button>
          }
        >
          Ten oddział nie ma jeszcze ustawionych godzin pracy. Uzupełnij grafik salonu, aby dodawać wizyty.
        </Alert>
      )}

      {/* ─── CONTENT ─── */}
      <Box sx={{ flex: { lg: 1 }, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2 }}>
        {isWide ? (
          <Paper
            component="aside"
            aria-label="Nawigacja i filtry kalendarza"
            sx={{ width: 272, flexShrink: 0, overflowY: 'auto', p: 2 }}
          >
            <Stack divider={<Divider flexItem />} gap={2}>
              {panelSections}
            </Stack>
          </Paper>
        ) : (
          <Collapse in={panelOpen} id="calendar-side-panel" unmountOnExit>
            <Paper component="aside" aria-label="Nawigacja i filtry kalendarza" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
                  alignItems: 'start',
                  '& > *': { maxWidth: { xs: 360, sm: 'none' } },
                }}
              >
                {panelSections}
              </Box>
            </Paper>
          </Collapse>
        )}

        {/* Calendar grid */}
        <Paper
          ref={gridRef}
          sx={{
            flex: { lg: 1 },
            minWidth: 0,
            overflow: 'auto',
            position: 'relative',
            height: { xs: 'calc(100vh - 88px)', lg: 'auto' },
            minHeight: { xs: 420, lg: 0 },
            overscrollBehaviorX: 'contain',
          }}
        >
          {staffMembers.length === 0 ? (
            <EmptyState
              icon={<GroupsOutlinedIcon />}
              title="Brak pracowników w kalendarzu"
              description={canEditSchedules
                ? 'Kalendarz pokazuje wizyty w kolumnach pracowników. Dodaj pracownika, żeby zacząć planować wizyty.'
                : 'Kalendarz pokazuje wizyty w kolumnach pracowników. Poproś właściciela salonu o dodanie zespołu.'}
              actions={canEditSchedules ? (
                <Button variant="outlined" onClick={() => navigate('/salon/staff')}>Przejdź do pracowników</Button>
              ) : undefined}
            />
          ) : viewMode === 'day' ? (
            /* ─── DAY VIEW ─── */
            <Box sx={{ minWidth: TIME_COL_WIDTH + visibleStaff.length * DAY_COL_MIN_WIDTH, position: 'relative' }}>
              {/* Staff column headers */}
              <Box
                sx={{
                  display: 'flex',
                  borderBottom: `1px solid ${tokens.lineStrong}`,
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 30,
                }}
              >
                <Box sx={{ width: TIME_COL_WIDTH, flexShrink: 0, borderRight: `1px solid ${tokens.line}`, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }} />
                {visibleStaff.map((s) => {
                  const staffCount = visibleEvents.filter((e) => e.staffId === s.id).length;
                  const info = getStaffWorkingInfo(s, selectedDate, findScheduleException(s.id, selectedDate));
                  const hoursLabel = info.isWorking
                    ? `${pad2(info.startHour)}:${pad2(info.startMin)}–${pad2(info.endHour)}:${pad2(info.endMin)}`
                    : 'Wolne';
                  const header = (
                    <Box
                      key={s.id}
                      onClick={canEditSchedules ? () => setScheduleStaff({ id: s.id, name: s.name }) : undefined}
                      onKeyDown={canEditSchedules ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setScheduleStaff({ id: s.id, name: s.name }); } } : undefined}
                      role={canEditSchedules ? 'button' : undefined}
                      tabIndex={canEditSchedules ? 0 : undefined}
                      aria-label={canEditSchedules ? `Edytuj grafik pracy: ${s.name}` : undefined}
                      sx={{
                        flex: 1,
                        minWidth: DAY_COL_MIN_WIDTH,
                        px: 1.25,
                        py: 1.125,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        borderRight: `1px solid ${tokens.line}`,
                        ...(canEditSchedules && {
                          cursor: 'pointer',
                          transition: 'background-color .15s ease',
                          '&:hover': { bgcolor: tokens.surface },
                          '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 },
                        }),
                      }}
                    >
                      <PersonAvatar firstName={s.firstName} lastName={s.lastName} seed={s.id} size={30} sx={{ bgcolor: s.color, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 650, lineHeight: 1.3 }}>
                          {s.firstName}
                          {/* Na telefonie kolumna jest waska — samo imie. */}
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> {s.lastName}</Box>
                        </Typography>
                        <Typography noWrap sx={{ fontSize: '0.75rem', color: tokens.muted, lineHeight: 1.35, fontVariantNumeric: 'tabular-nums' }}>
                          {hoursLabel}
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> · {countLabel(staffCount, 'wizyta', 'wizyty', 'wizyt')}</Box>
                        </Typography>
                      </Box>
                    </Box>
                  );
                  return canEditSchedules ? (
                    <Tooltip key={s.id} title="Kliknij, aby edytować grafik pracy" enterDelay={500}>
                      {header}
                    </Tooltip>
                  ) : header;
                })}
              </Box>
              {/* Time grid */}
              <Box sx={{ display: 'flex', position: 'relative' }}>
                {timeColumn}
                {/* Staff columns */}
                {visibleStaff.map((staff) => {
                  const absence = findAbsence(staff.id, selectedDate);
                  const scheduleException = findScheduleException(staff.id, selectedDate);
                  const workInfo = getStaffWorkingInfo(staff, selectedDate, scheduleException);
                  const isStaffWorking = workInfo.isWorking;
                  const workStartMin = isStaffWorking ? workInfo.startHour * 60 + workInfo.startMin : 0;
                  const workEndMin = isStaffWorking ? workInfo.endHour * 60 + workInfo.endMin : 0;
                  const columnLanes = computeLanes(visibleEvents.filter((e) => e.staffId === staff.id));

                  return (
                  <Box
                    key={staff.id}
                    data-staff-column={staff.id}
                    sx={{ flex: 1, position: 'relative', borderRight: `1px solid ${tokens.line}`, minWidth: DAY_COL_MIN_WIDTH, height: HOURS.length * HOUR_HEIGHT }}
                  >
                    {HOURS.map((h) => {
                      const slotTopMin = h * 60;
                      const slotHalfMin = h * 60 + 30;
                      const isTopBlocked = !isStaffWorking || slotTopMin < workStartMin || slotTopMin >= workEndMin;
                      const isBottomBlocked = !isStaffWorking || slotHalfMin < workStartMin || slotHalfMin >= workEndMin;

                      return (
                      <React.Fragment key={h}>
                        <Box
                          onMouseDown={() => !isTopBlocked && beginDrag(staff.id, (h - 7) * 2)}
                          onMouseEnter={() => !isTopBlocked && extendDrag(staff.id, (h - 7) * 2)}
                          sx={{
                            position: 'absolute',
                            top: (h - 7) * HOUR_HEIGHT,
                            left: 0,
                            right: 0,
                            height: HOUR_HEIGHT / 2,
                            ...slotLineSx(false),
                            cursor: isTopBlocked ? 'default' : 'pointer',
                            userSelect: 'none',
                            ...(isTopBlocked ? blockedSlotSx : { '&:hover': { bgcolor: alpha(staff.color, 0.05) } }),
                            transition: 'background-color 0.1s',
                          }}
                        />
                        <Box
                          onMouseDown={() => !isBottomBlocked && beginDrag(staff.id, (h - 7) * 2 + 1)}
                          onMouseEnter={() => !isBottomBlocked && extendDrag(staff.id, (h - 7) * 2 + 1)}
                          sx={{
                            position: 'absolute',
                            top: (h - 7) * HOUR_HEIGHT + HOUR_HEIGHT / 2,
                            left: 0,
                            right: 0,
                            height: HOUR_HEIGHT / 2,
                            ...slotLineSx(true),
                            cursor: isBottomBlocked ? 'default' : 'pointer',
                            userSelect: 'none',
                            ...(isBottomBlocked ? blockedSlotSx : { '&:hover': { bgcolor: alpha(staff.color, 0.05) } }),
                            transition: 'background-color 0.1s',
                          }}
                        />
                      </React.Fragment>
                      );
                    })}
                    {/* Bottom border */}
                    <Box sx={{ position: 'absolute', top: HOURS.length * HOUR_HEIGHT, left: 0, right: 0, ...slotLineSx(false) }} />
                    {/* Nieobecnosc pracownika w tym dniu — warstwa informacyjna.
                        Celowo pointerEvents:'none': wizyte nadal mozna dodac,
                        system tylko ostrzeze, zgodnie z reszta kalendarza. */}
                    {absence && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: HOURS.length * HOUR_HEIGHT,
                          ...absenceOverlaySx,
                          zIndex: 2,
                          pointerEvents: 'none',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                          pt: 1.25,
                        }}
                      >
                        <GridBadge
                          icon={<EventBusyIcon />}
                          label={`${ABSENCE_LABELS[absence.type]}${absence.status === 'PENDING' ? ' · niezatwierdzona' : ''}`}
                        />
                      </Box>
                    )}
                    {/* Wyjatek w grafiku (uwaga klienta 3). Wyszarzenie slotow
                        zalatwia juz getStaffWorkingInfo — tu zostaje sama
                        etykieta, zeby bylo wiadomo, skad ten ksztalt dnia.
                        Przy nieobecnosci pomijamy, zeby nie pietrzyc plakietek. */}
                    {scheduleException && !absence && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          zIndex: 2,
                          pointerEvents: 'none',
                          display: 'flex',
                          justifyContent: 'center',
                          pt: 1.25,
                        }}
                      >
                        <GridBadge
                          tone={scheduleException.isOff ? 'neutral' : 'plum'}
                          label={
                            scheduleException.isOff
                              ? scheduleException.reason || 'Dzień wolny'
                              : `Zmiana grafiku: ${scheduleException.startTime}–${scheduleException.endTime}`
                          }
                        />
                      </Box>
                    )}
                    {/* Drag selection highlight */}
                    {drag && drag.staffId === staff.id && (() => {
                      const lo = Math.min(drag.start, drag.end);
                      const hi = Math.max(drag.start, drag.end);
                      return (
                        <Box sx={{
                          position: 'absolute',
                          top: lo * (HOUR_HEIGHT / 2),
                          height: (hi - lo + 1) * (HOUR_HEIGHT / 2),
                          left: 3, right: 3,
                          bgcolor: alpha(staff.color, 0.14),
                          border: `1.5px solid ${staff.color}`,
                          borderRadius: '8px', zIndex: 3, pointerEvents: 'none',
                        }} />
                      );
                    })()}
                    {/* Events */}
                    {visibleEvents
                      .filter((e) => {
                        // Przeciagany kafel rysujemy w kolumnie, nad ktora jest
                        // kursor — dzieki temu widac takze zmiane pracownika.
                        if (moveDrag && e.id === moveDrag.eventId) return moveDrag.targetStaffId === staff.id;
                        return e.staffId === staff.id;
                      })
                      .map((event) => (
                        <EventCard
                          key={event.id}
                          event={event}
                          staff={staff}
                          onClick={handleEventClick}
                          startHourOffset={7}
                          onMoveStart={beginEventMove}
                          moveOffsetMin={moveDrag?.eventId === event.id ? moveDrag.deltaMin : 0}
                          isMoving={moveDrag?.eventId === event.id}
                          laneInfo={columnLanes[event.id]}
                        />
                      ))}
                  </Box>
                  );
                })}
                {/* Now line */}
                {showNowLine && toKey(now) === dayKey && (
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: nowPosition - 1,
                      left: TIME_COL_WIDTH - 5,
                      right: 0,
                      height: 2,
                      bgcolor: PREMIUM_COLORS.calendar.nowLine,
                      zIndex: 26,
                      pointerEvents: 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: -4,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: PREMIUM_COLORS.calendar.nowLine,
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          ) : (
            /* ─── WEEK VIEW ─── */
            <Box sx={{ minWidth: TIME_COL_WIDTH + 7 * WEEK_COL_MIN_WIDTH, position: 'relative' }}>
              {/* Day headers */}
              <Box
                sx={{
                  display: 'flex',
                  borderBottom: `1px solid ${tokens.lineStrong}`,
                  position: 'sticky',
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 30,
                }}
              >
                <Box sx={{ width: TIME_COL_WIDTH, flexShrink: 0, borderRight: `1px solid ${tokens.line}`, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }} />
                {weekDays.map((d, i) => {
                  const isToday = toKey(d) === toKey(now);
                  const isSel = toKey(d) === dayKey;
                  const dayEvtCount = filteredEvents.filter((e) => e.date === toKey(d)).length;
                  return (
                    <Box
                      key={i}
                      role="button"
                      tabIndex={0}
                      aria-label={`Pokaż dzień: ${formatDateLong(d)}`}
                      onClick={() => { setSelectedDate(d); setViewMode('day'); }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDate(d); setViewMode('day'); } }}
                      sx={{
                        flex: 1,
                        py: 1,
                        textAlign: 'center',
                        borderRight: `1px solid ${tokens.line}`,
                        cursor: 'pointer',
                        minWidth: WEEK_COL_MIN_WIDTH,
                        transition: 'background-color .15s ease',
                        '&:hover': { bgcolor: tokens.surface },
                        '&:focus-visible': { outline: `2px solid ${tokens.plum}`, outlineOffset: -2 },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isToday ? tokens.plum : tokens.muted, lineHeight: 1.4 }}>
                        {DAY_NAMES_SHORT[i]}
                      </Typography>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          mx: 'auto',
                          my: 0.25,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: '1.125rem',
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          color: isToday ? tokens.paper : isSel ? tokens.plum : tokens.ink,
                          bgcolor: isToday ? tokens.plum : isSel ? tokens.plumSoft : 'transparent',
                        }}
                      >
                        {d.getDate()}
                      </Box>
                      <Typography sx={{ fontSize: '0.75rem', color: dayEvtCount ? tokens.inkSoft : tokens.subtle, lineHeight: 1.4 }}>
                        {dayEvtCount ? countLabel(dayEvtCount, 'wizyta', 'wizyty', 'wizyt') : 'Brak wizyt'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {/* Time grid for week */}
              <Box sx={{ display: 'flex', position: 'relative' }}>
                {timeColumn}
                {/* Day columns */}
                {weekDays.map((d, dayIdx) => {
                  const dk = toKey(d);
                  const isToday = dk === toKey(now);
                  // Compute union of all staff working hours for this day
                  const dayWorkInfos = visibleStaff.map((s) =>
                    getStaffWorkingInfo(s, d, findScheduleException(s.id, d)),
                  );
                  const anyWorking = dayWorkInfos.some((w) => w.isWorking);
                  const unionStartMin = anyWorking ? Math.min(...dayWorkInfos.filter((w) => w.isWorking).map((w) => w.startHour * 60 + w.startMin)) : 0;
                  const unionEndMin = anyWorking ? Math.max(...dayWorkInfos.filter((w) => w.isWorking).map((w) => w.endHour * 60 + w.endMin)) : 0;
                  const dayLanes = computeLanes(visibleEvents.filter((e) => e.date === dk));

                  return (
                    <Box
                      key={dayIdx}
                      sx={{
                        flex: 1,
                        position: 'relative',
                        borderRight: `1px solid ${tokens.line}`,
                        minWidth: WEEK_COL_MIN_WIDTH,
                        height: HOURS.length * HOUR_HEIGHT,
                        bgcolor: isToday ? alpha(tokens.plum, 0.025) : 'transparent',
                      }}
                    >
                      {HOURS.map((h) => {
                        const slotTopMin = h * 60;
                        const slotHalfMin = h * 60 + 30;
                        const isTopBlocked = !anyWorking || slotTopMin < unionStartMin || slotTopMin >= unionEndMin;
                        const isBottomBlocked = !anyWorking || slotHalfMin < unionStartMin || slotHalfMin >= unionEndMin;

                        return (
                        <React.Fragment key={h}>
                          <Box
                            onClick={() => !isTopBlocked && handleSlotClick(visibleStaff[0]?.id || '1', dk, h, false)}
                            sx={{
                              position: 'absolute',
                              top: (h - 7) * HOUR_HEIGHT,
                              left: 0,
                              right: 0,
                              height: HOUR_HEIGHT / 2,
                              ...slotLineSx(false),
                              cursor: isTopBlocked ? 'default' : 'pointer',
                              ...(isTopBlocked ? blockedSlotSx : { '&:hover': { bgcolor: tokens.plumSofter } }),
                            }}
                          />
                          <Box
                            onClick={() => !isBottomBlocked && handleSlotClick(visibleStaff[0]?.id || '1', dk, h, true)}
                            sx={{
                              position: 'absolute',
                              top: (h - 7) * HOUR_HEIGHT + HOUR_HEIGHT / 2,
                              left: 0,
                              right: 0,
                              height: HOUR_HEIGHT / 2,
                              ...slotLineSx(true),
                              cursor: isBottomBlocked ? 'default' : 'pointer',
                              ...(isBottomBlocked ? blockedSlotSx : { '&:hover': { bgcolor: tokens.plumSofter } }),
                            }}
                          />
                        </React.Fragment>
                        );
                      })}
                      <Box sx={{ position: 'absolute', top: HOURS.length * HOUR_HEIGHT, left: 0, right: 0, ...slotLineSx(false) }} />
                      {/* Events — stacked by staff color */}
                      {visibleEvents
                        .filter((e) => e.date === dk)
                        .map((event) => {
                          const s = staffMembers.find((st) => st.id === event.staffId) || staffMembers[0];
                          return <EventCard key={event.id} event={event} staff={s} onClick={handleEventClick} startHourOffset={7} laneInfo={dayLanes[event.id]} />;
                        })}
                      {/* Now line for week */}
                      {showNowLine && isToday && (
                        <Box
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            top: nowPosition - 1,
                            left: 0,
                            right: 0,
                            height: 2,
                            bgcolor: PREMIUM_COLORS.calendar.nowLine,
                            zIndex: 4,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* ─── DIALOGS ─── */}
      <EventDetailDialog
        event={detailEvent}
        staff={staffMembers.find((s) => s.id === detailEvent?.staffId)}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onStatusChange={handleStatusChange}
        onSetPayment={handleSetPayment}
        onFinalize={handleFinalize}
        services={apiServices}
        products={products}
        paymentMethods={activePaymentMethods}
        onDelete={handleDeleteEvent}
        onEdit={(e) => setEditEvent(e)}
      />

      <AbsenceDialog
        open={absenceDialogOpen}
        onClose={() => setAbsenceDialogOpen(false)}
        staff={staffMembers}
        defaultDate={toKey(selectedDate)}
        onSaved={() => {
          refreshAbsences();
          setSnackbar({ open: true, message: 'Nieobecność zapisana', severity: 'success' });
        }}
      />

      <EditBookingDialog
        open={Boolean(editEvent)}
        event={editEvent}
        staff={staffMembers}
        onClose={() => setEditEvent(null)}
        onSave={(values) => applyEventEdit(editEvent as CalendarEvent, values, false)}
      />

      <NewBookingDialog
        open={newBookingOpen}
        onClose={() => { setNewBookingOpen(false); setNewBookingPrefill(undefined); }}
        onSave={handleNewBookingSave}
        staff={staffMembers}
        services={apiServices}
        clients={apiClients}
        salonId={salonId}
        onClientCreated={(client) => setApiClients((prev) => [client, ...prev])}
        prefill={newBookingPrefill}
      />

      <WorkingHoursEditor
        salonId={activeSalonId}
        open={hoursEditorOpen}
        onClose={() => setHoursEditorOpen(false)}
        onSaved={refetchWorkingHours}
      />

      {/* Potwierdzenie zapisu wizyty mimo ostrzezen (kolizja, grafik, godziny otwarcia). */}
      <Dialog
        open={Boolean(pendingConfirm)}
        onClose={() => !confirmingAction && setPendingConfirm(null)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="pending-confirm-title"
      >
        <DialogTitle id="pending-confirm-title">{pendingConfirm?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.9375rem', color: tokens.inkSoft, mb: 1.5 }}>
            {pendingConfirm && pendingConfirm.warnings.length > 1
              ? 'Termin budzi następujące zastrzeżenia:'
              : 'Termin budzi zastrzeżenie:'}
          </Typography>
          <Stack spacing={1}>
            {pendingConfirm?.warnings.map((w) => (
              <Alert key={w.code} severity="warning">
                {w.message}
              </Alert>
            ))}
          </Stack>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, mt: 2 }}>
            Możesz kontynuować mimo to — decyzja należy do Ciebie.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)} disabled={confirmingAction}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmPendingAction}
            disabled={confirmingAction}
            startIcon={confirmingAction ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {confirmingAction ? 'Zapisywanie…' : 'Kontynuuj mimo to'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grafik pracy — otwierany kliknieciem w naglowek kolumny pracownika. */}
      {scheduleStaff && (
        <StaffScheduleDialog
          open
          staffId={scheduleStaff.id}
          staffName={scheduleStaff.name}
          onClose={() => setScheduleStaff(null)}
          onSave={() => {
            refreshStaffSchedules();
            setSnackbar({ open: true, message: 'Grafik pracy zapisany', severity: 'success' });
          }}
        />
      )}

      {/* Na telefonie glowna akcja jest zawsze pod kciukiem. */}
      <Fab
        color="primary"
        aria-label={newBookingLabel}
        onClick={openNewBooking}
        sx={{ position: 'fixed', bottom: 24, right: 20, zIndex: 40, display: { xs: 'flex', sm: 'none' } }}
      >
        <AddIcon />
      </Fab>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
